import * as React from "react";
import jsPDF from "jspdf";
// eslint-disable-next-line
import lodashGet from "lodash/get";
import { addTable } from "../../helpers/pdfHelper";

import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import { minValue, maxValue } from "@jazasoft/mui-table";

import {
  getDistinctObjectValues,
  getDistinctValues,
  getWeightedMean,
  isEmpty,
  isFalse,
  isTrue,
  listFromCsv,
  mapFromCsv,
  signedValue,
  toFixed,
  toFixedSigned,
  toFixedValue,
  truncate
} from "../../utils/helpers";
import {
  cbMarkerSort,
  cbSortFlow,
  findSizeBreakupList,
  getCutOrderFlowInfoList,
  getEffFlowInfoList,
  getEffPartList,
  getEffectiveExtraPercent,
  getOrderFlowList,
  getPartType
} from "../../helpers/orderHelper";
import { getRepeatSize } from "../../helpers/warehouseHelper";

import { AlertParemeter, SpreadingType, MarkerType, OState, ReserveType } from "../../utils/types";
import {
  DEFAULT_SEPARATOR,
  LotType,
  MiscType,
  MULTI_PART_EXACT_SAME_BUNDLE,
  MULTI_PART_MANUAL_PLAN,
  OrderFlowType,
  PART_SHELL,
  STATE_PREV_LOT_CUTTING_COMPLETED,
  STATE_PREV_LOT_CUTTING_NOT_COMPLETED,
  UOM
} from "../../constants";
import { getMarkerGroupName } from "../grouping/groupingHelper";
import { REGEX_POSITIVE_DECIMAL, REGEX_POSITIVE_NUMBER } from "../../utils/regex";
import { setDefault, minReqHeight, totalPagesExp } from "../../helpers/pdfHelper";
import { colorTomatoRed } from "../../utils/colors";
import { convert } from "../../helpers/unitConversionHelper";
import Tooltip from "../../components/Tooltip";
import { PARAMETER_CONFIG } from "./cutplan-view/VarianceReport";

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const joinChar = "#@#";
const getDataKey = dataKey => dataKey?.replace(/\./g, "_");

const fpoIdentifier = record => `${record?.primaryUnitId || record?.unitId || ""}${joinChar}${record?.fpo || ""}`;

export const choicesParameter = Object.values(AlertParemeter);

const renderPly = (value, shortValue, row) => {
  const plies = !isEmpty(row?.shellPlies) ? row?.shellPlies : row?.plies;
  const plannedPlies = !isEmpty(row?.spreadPlies) ? row?.spreadPlies : row?.plies;
  const shortPlies = (plies || 0) - (plannedPlies || 0);
  return (
    <div>
      {plies || "-"}
      {(!isEmpty(row?.shellPlies) || !isEmpty(row?.spreadPlies)) && shortPlies > 0 && (
        <sub style={{ fontSize: 10 }}>
          <ArrowDownwardIcon style={{ color: "red", fontSize: 10 }} /> {shortPlies}
        </sub>
      )}
    </div>
  );
};

const NON_SB_FIELDS = ["style", "color", "destination", "delivery", "total"];

const destKey = flowInfo =>
  `${flowInfo.flowRef || "FR"}-${flowInfo.customerFlowRef || "CFR"}-${flowInfo.inseam || "IN"}-${flowInfo.destination || "DEST"}-${
    flowInfo.deliveryDate || "DEL"
  }`;

const csvField = (flowInfoList, field) => getDistinctValues(flowInfoList.map(fi => fi[field]).filter(e => !isEmpty(e))).join(", ");

export const humanizeMarkerName = markerName => {
  if (isEmpty(markerName)) return markerName;

  // Split into key-value pairs like ["G1_S:1", "G1_L:2", ...]
  const pairs = markerName.split(",").map(s => s.trim());

  const groups = {};

  for (const pair of pairs) {
    const [key, value] = pair.split(":").map(s => s.trim());
    const [group, size] = key.split("_");

    if (!groups[group]) groups[group] = [];
    groups[group].push(`${size}:${value}`);
  }

  // Join each group into the desired string format
  const entries = Object.entries(groups);
  return entries.map(([group, items]) => (entries.length === 1 && group === "G1" ? items.join(", ") : `${group} -> ${items.join(", ")}`)).join(" | ");
};

//* Extract only key from flowInfo which are needed to shown in columns
export const reduceFlowInfo = (flowInfoList, order) => {
  if (isEmpty(flowInfoList)) return {};
  const flowInfo = flowInfoList[0];
  const productList = getDistinctObjectValues(flowInfoList.map(e => e.product));
  const units = getDistinctValues(flowInfoList.map(e => e.primaryUnitId));
  const fpos = getDistinctValues(flowInfoList.map(e => e.fpo));
  const extFpos = getDistinctValues(flowInfoList.map(e => e.externalFpo));

  const parentProductList = getDistinctObjectValues(
    flowInfoList
      .map(fi => order?.flowInfoList?.find(e => e.id === fi.parentId))
      .filter(e => !isEmpty(e))
      .map(e => e.product)
  );
  const uomQty = productList.length > 1 && parentProductList.length > 0 && parentProductList.some(e => isTrue(e.set)) ? "Set" : "Pc";

  let orderQty = getDistinctObjectValues(flowInfoList)?.reduce((acc, fi) => acc + fi.orderQty, 0);
  let fpoQty = getDistinctObjectValues(flowInfoList)?.reduce((acc, fi) => acc + (fi.fpoQty || 0), 0);
  if (uomQty === "Set") {
    const product = productList[0];
    const pfiList = flowInfoList.filter(e => e.product?.id === product?.id);
    const ratio = pfiList[0]?.ratio || 1;
    orderQty = pfiList.reduce((acc, fi) => acc + fi.orderQty, 0) / ratio;
    fpoQty = pfiList.reduce((acc, fi) => acc + fi.fpoQty, 0) / ratio;
  }

  return {
    product: productList.length === 1 ? flowInfo.product : null,
    productName: productList.map(e => e.name).join(", "),
    style: getDistinctValues(flowInfoList.map(fi => fi.style)).join(", "),
    fit: getDistinctValues(flowInfoList.map(fi => fi.fit).filter(e => !isEmpty(e))).join(", "),
    color: getDistinctValues(flowInfoList.map(fi => fi.color)).join(", "),
    styleColorList: getDistinctValues(flowInfoList.map(fi => fi.styleColor)),
    unitId: units?.length === 1 ? units[0] : null,
    fpo: fpos?.length === 1 ? fpos[0] : null,
    externalFpo: extFpos?.length === 1 ? extFpos[0] : null,
    ...["inseam", "destination", "deliveryDate", "delMode", "customerFlowRef", "flowRef"].reduce(
      (acc, field) => ({ ...acc, [field]: csvField(flowInfoList, field) }),
      {}
    ),
    orderQty,
    fpoQty,
    extra: getEffectiveExtraPercent(flowInfoList),
    uomQty
  };
};

export const getReadyToPlanRows = order => {
  if (isEmpty(order?.orderFlowList) || isEmpty(order?.flowInfoList)) return [];
  const rowList = order.orderFlowList
    .filter(e => e.type === OrderFlowType.GROUPING && e.state === OState.FINAL_GROUPING_COMPLETED.state)
    .sort((a, b) => b.id - a.id)
    .flatMap(groupOrder => {
      const flowInfoList = order.flowInfoList
        .filter(
          fi =>
            groupOrder.productIdList?.includes(fi.product?.id) &&
            groupOrder.styleColorList?.includes(fi.styleColor) &&
            fpoIdentifier(groupOrder) === fpoIdentifier(fi)
        )
        .flatMap(fi => {
          let sbList = fi.sizeBreakupList.filter(sb => sb.type === "ORDER");
          if (isEmpty(sbList)) {
            const parent = order.flowInfoList.find(e => fi.orderParentId === e.id);
            sbList = parent?.sizeBreakupList.filter(sb => sb.type === "ORDER") || [];
            if (isEmpty(sbList)) {
              const childList = order.flowInfoList
                .filter(e => e.parentId === parent?.id)
                .filter(e => isEmpty(fi.flowRef) || e.flowRef === fi.flowRef)
                .filter(e => isEmpty(fi.delFlowRef) || e.delFlowRef === fi.delFlowRef)
                .filter(e => isEmpty(fi.customerFlowRef) || e.customerFlowRef === fi.customerFlowRef)
                .filter(e => isEmpty(fi.destination) || e.destination === fi.destination)
                .filter(e => isEmpty(fi.deliveryDate) || e.deliveryDate === fi.deliveryDate)
                .filter(e => isEmpty(fi.delMode) || e.delMode === fi.delMode);
              sbList = childList.flatMap(e => e.sizeBreakupList || []).filter(sb => sb.type === "ORDER");
            }
          }

          const sizeGroups = getDistinctValues(sbList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)));
          if (!isEmpty(fi.inseam) || isEmpty(sizeGroups) || sizeGroups?.length <= 1) {
            return [{ ...fi, orderQty: sbList.reduce((acc, sb) => acc + sb.qty, 0), sizeBreakupList: sbList }];
          } else {
            return sizeGroups.map(sizeGroup => {
              const cSbList = sbList.filter(sb => sb.sizeGroup === sizeGroup);
              const fpoSbList = fi.fpoSizeBreakupList?.filter(sb => sb.sizeGroup === sizeGroup);
              const orderQty = cSbList.reduce((acc, sb) => acc + sb.qty, 0);
              const fpoQty = fpoSbList.reduce((acc, sb) => acc + sb.qty, 0);
              return { ...fi, inseam: sizeGroup, orderQty, fpoQty, sizeBreakupList: cSbList, fpoSizeBreakupList: fpoSbList };
            });
          }
        });
      const cutOrderList = order.orderFlowList.filter(fi => fi.parentId === groupOrder.id);
      const groupAllocationQty = groupOrder.allocatedQty;
      const cutAllocationQty = cutOrderList.reduce((acc, e) => acc + e.allocatedQty, 0);

      //** If Cut Order is not created for all Group Order Fabric **/
      if (groupAllocationQty - cutAllocationQty > 1) {
        const destKeyList = getDistinctValues(flowInfoList.map(flowInfo => destKey(flowInfo)));
        let childList = [];
        if (destKeyList?.length > 1) {
          childList = destKeyList.map(dKey => {
            const fiList = flowInfoList.filter(fi => dKey === destKey(fi));
            const flowInfo = fiList[0] || {};
            const partList = getEffPartList(fiList, order);
            const part = partList?.find(p => p.effPartName === groupOrder.part) || {};
            const childCutOrderList = cutOrderList
              .filter(c => isEmpty(c.flowRefs) || listFromCsv(c.flowRefs).includes(flowInfo.flowRef))
              .filter(c => isEmpty(c.customerFlowRefs) || listFromCsv(c.customerFlowRefs).includes(flowInfo.customerFlowRef))
              .filter(c => isEmpty(c.inseams) || listFromCsv(c.inseams).includes(flowInfo.inseam))
              .filter(c => isEmpty(c.destinations) || listFromCsv(c.destinations).includes(flowInfo.destination))
              .filter(c => isEmpty(c.deliveries) || listFromCsv(c.deliveries).includes(flowInfo.deliveryDate))
              .filter(c => isEmpty(c.delModes) || listFromCsv(c.delModes).includes(flowInfo.delMode));

            return {
              key: fiList.map(fi => `${groupOrder?.id}-${fi.id}-${part?.effPartName}-${fi.inseam}`).join("_"),
              parentKey: groupOrder.id,
              ...reduceFlowInfo(fiList, order),
              partList,
              style: groupOrder.styles,
              styleColor: groupOrder?.colors,
              effPartName: groupOrder.part,
              bomCu: part.bomCu,
              ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
              uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom,
              lotValue: groupOrder.lotValue,
              groupOrder,
              cutOrderList: childCutOrderList,
              flowInfoList: fiList
            };
          });
        }

        const partList = getEffPartList(flowInfoList, order);
        const part = partList?.find(p => p.effPartName === groupOrder.part) || {};
        return [
          {
            key: groupOrder.id,
            ...reduceFlowInfo(flowInfoList, order),
            partList,
            style: groupOrder.styles,
            styleColor: groupOrder?.colors,
            effPartName: groupOrder.part,
            bomCu: part?.bomCu,
            ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
            uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom,
            lotValue: groupOrder.lotValue,
            groupOrder,
            cutOrderList,
            flowInfoList
          },
          ...childList
        ];
      }
      return [];
    });

  return rowList;
};

export const getCutOrderRows = (order, productParts, lotType, cutOrder, page, ledgerList) => {
  if (isEmpty(order?.orderFlowList) || isEmpty(order?.flowInfoList)) return [];
  const partSerial = !isEmpty(productParts) ? listFromCsv(productParts).reduce((acc, part, idx) => ({ ...acc, [part]: idx }), {}) : {};

  const setting = cutOrder?.orderConfig?.setting;

  let cutOrderList = [];
  if (!isEmpty(cutOrder)) {
    // Include Other Cut Orders for Part, Product and Lot
    if (page === "cutPlan") {
      // Include Cut Orders for Part
      const cutOrderList1 = order.orderFlowList
        .filter(e => e.type === OrderFlowType.CUTPLAN)
        .filter(
          e =>
            cutOrder.productIdList?.every(id => e.productIdList?.includes(id)) &&
            e.styleColorList.some(styleColor => cutOrder.styleColorList.includes(styleColor)) &&
            e.lotValue === cutOrder.lotValue &&
            fpoIdentifier(e) === fpoIdentifier(cutOrder)
        )
        .filter(
          e =>
            (isEmpty(e.inseams) && isEmpty(cutOrder.inseams)) || listFromCsv(e.inseams).some(inseam => listFromCsv(cutOrder.inseams).includes(inseam))
        )
        .filter(
          e =>
            (isEmpty(e.destinations) && isEmpty(cutOrder.destinations)) ||
            listFromCsv(e.destinations).some(destination => listFromCsv(cutOrder.destinations).includes(destination))
        )
        .filter(
          e =>
            (isEmpty(e.deliveries) && isEmpty(cutOrder.deliveries)) ||
            listFromCsv(e.deliveries).some(delivery => listFromCsv(cutOrder.deliveries).includes(delivery))
        );

      // Include Cut Orders for Product && Part
      let cutOrderList2 = [];
      if (!isEmpty(cutOrder.parentColor)) {
        cutOrderList2 = order.orderFlowList
          .filter(e => e.type === OrderFlowType.CUTPLAN)
          .filter(
            e =>
              e.parentProductId === cutOrder.parentProductId &&
              e.styles === cutOrder.styles &&
              e.parentColor === cutOrder.parentColor &&
              e.lotValue === cutOrder.lotValue &&
              fpoIdentifier(e) === fpoIdentifier(cutOrder)
          )
          .filter(
            e =>
              (isEmpty(e.inseams) && isEmpty(cutOrder.inseams)) ||
              listFromCsv(e.inseams).some(inseam => listFromCsv(cutOrder.inseams).includes(inseam))
          )
          .filter(
            e =>
              (isEmpty(e.destinations) && isEmpty(cutOrder.destinations)) ||
              listFromCsv(e.destinations).some(destination => listFromCsv(cutOrder.destinations).includes(destination))
          )
          .filter(
            e =>
              (isEmpty(e.deliveries) && isEmpty(cutOrder.deliveries)) ||
              listFromCsv(e.deliveries).some(delivery => listFromCsv(cutOrder.deliveries).includes(delivery))
          );
      }
      cutOrderList = getDistinctObjectValues([cutOrder, ...cutOrderList1, ...cutOrderList2]);
    } else {
      cutOrderList.push(cutOrder);
    }
  }
  const cutOrderIds = cutOrderList.map(e => e.id);

  let rowList = order.orderFlowList
    .filter(e => e.type === OrderFlowType.CUTPLAN)
    .filter(e => isEmpty(lotType) || lotType === e.lotType)
    .filter(e => isEmpty(cutOrderIds) || cutOrderIds.includes(e.id))
    .sort((a, b) => cbSortFlow(a, b, partSerial))
    .flatMap(cutOrder => {
      const flowInfoList = getCutOrderFlowInfoList(order, cutOrder);
      const destKeyList = getDistinctValues(flowInfoList.map(flowInfo => destKey(flowInfo)));
      let childList = [];
      if (destKeyList?.length > 1) {
        childList = destKeyList.map(dKey => {
          const fiList = flowInfoList.filter(fi => dKey === destKey(fi));
          const part = getEffPartList(fiList, order).find(p => p.effPartName === cutOrder.part) || {};
          const ledgers = fiList.flatMap(flowInfo => ledgerList?.filter(e => e.style === flowInfo.style && e.styleColor === flowInfo.color) || []);
          const ledger = ledgers[0] || {};
          const fabric = ledger?.fabric || {};

          return {
            key: `${cutOrder.id}-${fiList.map(fi => `${fi.id}-${fi.inseam || ""}`).join("-")}`,
            parentKey: cutOrder.id,
            ...reduceFlowInfo(fiList, order),
            poRef: order?.poRef,
            customerName: order?.customer?.name,
            seasonName: order?.season?.name,
            // style: cutOrder.styles,
            // styleColor: cutOrder?.colors,
            effPartName: cutOrder.part,
            bomCu: part?.bomCu,
            ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
            uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom,
            lotValue: cutOrder.lotValue,
            cutOrder,
            flowInfoList: fiList,
            itemCode: fabric.itemCode,
            fabColor: ledger.fabColor
          };
        });
      }

      const part = getEffPartList(flowInfoList, order).find(p => p.effPartName === cutOrder.part) || {};

      const ledgers = flowInfoList.flatMap(flowInfo => ledgerList?.filter(e => e.style === flowInfo.style && e.styleColor === flowInfo.color) || []);
      const ledger = ledgers[0] || {};
      const fabric = ledger?.fabric || {};

      return [
        {
          key: cutOrder.id,
          ...reduceFlowInfo(flowInfoList, order),
          poRef: order?.poRef,
          customerName: order?.customer?.name,
          seasonName: order?.season?.name,
          // style: cutOrder.styles,
          // styleColor: cutOrder?.colors,
          effPartName: cutOrder.part,
          bomCu: part?.bomCu,
          ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
          uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom,
          lotValue: cutOrder.lotValue,
          cutOrder,
          flowInfoList,
          itemCode: fabric.itemCode,
          fabColor: ledger.fabColor,
          cutPlanRemarks: setting?.cutPlanRemarks
        },
        ...childList
      ];
    });

  return rowList.map(e => ({ ...e, state: e.cutOrder?.state }));
};

export const getColumnsSizeBreakup = (cutOrder, order) => {
  if (isEmpty(cutOrder) || isEmpty(order)) return { columns: [], headerRows: [] };

  const flowInfoList = getCutOrderFlowInfoList(order, cutOrder);

  const styleCount = getDistinctValues(flowInfoList?.map(fi => fi.style))?.length;
  const colorCount = getDistinctValues(flowInfoList?.map(fi => fi.color))?.length;

  const sbList = findSizeBreakupList(cutOrder, order);

  const sizeGroups = getDistinctValues(sbList.sort((a, b) => a.serialNo - b.serialNo).map(sb => sb.sizeGroup));
  const styleColorIndex = sizeGroups.reduce((acc, e, idx) => ({ ...acc, [e]: idx % 2 }), {});
  const groupedCutPlan = getDistinctValues(sbList.filter(sb => !isEmpty(sb.sizeGroup)).map(sb => sb.sizeGroup))?.length > 1;

  let columns = [];
  if (styleCount > 1) {
    columns.push({ dataKey: "style", title: "Style", align: "center" });
  }
  if (colorCount > 1) {
    columns.push({ dataKey: "color", title: "Style Color", align: "center" });
  }

  let groupRow = {};
  let sizeList = [];
  let sizeGroup;
  for (let i = 0; i < sbList.length; i++) {
    const sb = sbList[i];
    const sizeKey = sb.sizeKey;
    let leftBorder = false;
    if (sizeGroup !== sb.sizeGroup) {
      leftBorder = true;
      sizeGroup = sb.sizeGroup;
      groupRow[sizeKey] = sb.sizeGroup;
    }
    sizeList.push({
      dataKey: sizeKey,
      title: sizeKey.split("_")[1],
      element: ({ record, column }) => <span>{record[column.dataKey] || "-"}</span>,
      styleColorIdx: sizeGroups.length > 1 ? styleColorIndex[sb.sizeGroup] : undefined,
      leftBorder
    });
  }

  columns = [...columns, ...sizeList, { dataKey: "total", title: "Total", leftBorder: true }];
  columns = columns.map(c => ({
    ...c,
    align: "center",
    colSpan: ({ column, row, rowIdx }) =>
      !groupedCutPlan || rowIdx === 1 || NON_SB_FIELDS.includes(column.dataKey)
        ? 1
        : isEmpty(row[column.dataKey])
          ? 0
          : getDistinctValues(sbList.filter(sb => sb.sizeGroup === row[column.dataKey]).map(sb => sb.sizeKey))?.length
  }));

  let headerRows = [];
  if (Object.keys(groupRow)?.length > 1) {
    headerRows = [groupRow, columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
  } else {
    headerRows = [columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
  }

  return { columns, headerRows };
};

export const getRowsSizeBreakup = (flowInfoList, order) => {
  const productList = getDistinctObjectValues(flowInfoList.map(e => e.product));
  const parentProductList = getDistinctObjectValues(
    flowInfoList
      .map(fi => order?.flowInfoList?.find(e => e.id === fi.parentId))
      .filter(e => !isEmpty(e))
      .map(e => e.product)
  );
  const uomQty = productList.length > 1 && parentProductList.length > 0 && parentProductList.some(e => isTrue(e.set)) ? "Set" : "Pc";

  const styleColorList = getDistinctValues(flowInfoList.map(fi => `${fi.style}-${fi.color}`));

  let rows = styleColorList.map(styleColor => {
    let fiList = flowInfoList.filter(fi => `${fi.style}-${fi.color}` === styleColor);
    if (uomQty === "Set") {
      const product = productList[0];
      fiList = fiList.filter(fi => fi.product?.id === product?.id);
    }

    const fi = fiList[0];
    const ratio = fi.ratio || 1;
    const sbList = fiList.sort((a, b) => a.serialNo - b.serialNo).flatMap(fi => fi.fpoSizeBreakupList || []);
    return {
      style: fi.style,
      color: fi.color,
      total: sbList?.reduce((acc, sb) => acc + sb.qty, 0),
      ...sbList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: (acc[sb.sizeKey] || 0) + sb.qty / ratio }), {})
    };
  });
  if (rows.every(row => row.total === 0)) {
    rows = [];
  }

  return rows;
};

export const getColumnsMarkerDetails = (order, orderFlow, enablePerimeterUpdate = false) => {
  const manualCutPlan = orderFlow?.orderConfig?.multiPartBundling === MULTI_PART_MANUAL_PLAN;
  const setting = orderFlow?.orderConfig?.setting || {};

  const sbList = findSizeBreakupList(orderFlow, order);
  const sizeGroups = getDistinctValues(sbList.sort((a, b) => a.serialNo - b.serialNo).map(sb => sb.sizeGroup));
  const styleColorIndex = sizeGroups.reduce((acc, e, idx) => ({ ...acc, [e]: idx % 2 }), {});

  const groupedCutPlan = sizeGroups?.length > 1;

  let sizeGroup;
  let groupRow = {};
  const sizeList = sbList.map((sb, idx) => {
    let leftBorder = false;
    if (sizeGroup !== sb.sizeGroup) {
      leftBorder = true;
      sizeGroup = sb.sizeGroup;
      groupRow[sb.sizeKey] = sizeGroup;
    }
    return {
      dataKey: getDataKey(sb.sizeKey),
      title: sb.size,
      align: "center",
      styleColorIdx: sizeGroups.length > 1 ? styleColorIndex[sb.sizeGroup] : undefined,
      leftBorder,
      rightBorder: idx === sbList.length - 1,
      headerCellProps: { style: { borderLeft: leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined } },
      rowCellProps: {
        style: {
          borderLeft: leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
          borderRight: idx === sbList.length - 1 ? "1px solid rgba(224, 224, 224, 1)" : undefined
        }
      },
      colSpan: ({ column, row, rowIdx }) =>
        !groupedCutPlan || rowIdx === 1
          ? 1
          : isEmpty(row[column.dataKey])
            ? 0
            : getDistinctValues(sbList.filter(sb => sb.sizeGroup === row[column.dataKey]).map(sb => sb.sizeKey))?.length
    };
  });

  let columnList = [
    {
      dataKey: "markerNo",
      title: "M. No.",
      render: (value, shortValue, row) =>
        isEmpty(row) || isEmpty(value)
          ? ""
          : isEmpty(row.markerType)
            ? value
            : `M${row.markerNo}${[MarkerType.REMNANT, MarkerType.REMNANT_RETAINED].includes(row.markerType) ? "*" : ""}`
    },
    // { dataKey: "name", title: "Marker" },
    ...sizeList,
    { dataKey: "way", title: "Way", headerCellProps: { style: { borderLeft: "1px solid rgba(224, 224, 224, 1)" } } },
    { dataKey: "plies", title: "Plies", render: renderPly },
    { dataKey: "qty", title: "Qty" },
    { dataKey: "qtyPercentage", title: "Qty(%)" },
    {
      dataKey: "markerLength",
      title: "Marker \nLength",
      inputType: "text-input",
      options: { type: "number" },
      validate: [minValue(0, "Must be at least 0")],
      disabled: (row, dataKey) => orderFlow?.lotType === LotType.BULK && isTrue(row?.completed) && !manualCutPlan,
      render: value => (!isEmpty(value) ? `${value} m` : "")
    },
    {
      dataKey: "efficiency",
      title: "Efficiency",
      inputType: "text-input",
      options: { type: "number" },
      validate: [minValue(0, "Must be at least 0"), maxValue(100, "Must be 100 or less")],
      disabled: (row, dataKey) => orderFlow?.lotType === LotType.BULK && isTrue(row?.completed) && !manualCutPlan,
      render: value => (!isEmpty(value) ? `${value}%` : "")
    },
    {
      dataKey: "perimeter",
      title: "Perimeter",
      inputType: "text-input",
      options: { type: "number" },
      validate: [minValue(0, "Must be at least 0")],
      // disabled: (row, dataKey) => orderFlow?.lotType === LotType.BULK && isTrue(row?.completed) && !manualCutPlan, //TODO: Review
      render: value => (!isEmpty(value) ? `${value} m` : "")
    },
    {
      dataKey: "cplWarpShrPercent",
      title: "CPL Warp \nShrinkage",
      render: value => (!isEmpty(value) ? `${value}%` : "")
    },
    {
      dataKey: "layLength",
      title: "Lay \nLength",
      render: (value, _, row) => (!isEmpty(value) && !isEmpty(row?.length) ? `${value} m` : "")
    },
    { dataKey: "fabric", title: "Fabric", render: value => (!isEmpty(value) && Number(value) > 0 ? `${value} m` : "") },
    {
      dataKey: "balanceFabric",
      title: "Balance Fabric",
      render: value => (
        <span
          style={{
            backgroundColor: !isEmpty(value) && parseFloat(value) < 0 ? colorTomatoRed.light : "none",
            padding: !isEmpty(value) && parseFloat(value) < 0 ? "4px 8px 4px 1px" : undefined
          }}>
          {value}
        </span>
      )
    },
    {
      dataKey: "cplWeftShrPercent",
      title: "CPL Weft \nShrinkage",
      render: value => (!isEmpty(value) ? `${value}%` : "")
    },
    { dataKey: "groupName", title: "Group", align: "left" },
    { dataKey: "width", title: "Width (CM)" },
    { dataKey: "patternValue", title: "Pattern Value" },
    { dataKey: "patternNumber", title: "Pattern No." },
    { dataKey: "csv", title: "CSV" },
    { dataKey: "shade", title: "Shade" },
    { dataKey: "prodType", title: "Production Type" },
    { dataKey: "remarks", title: "Remarks" }
  ];
  if (!manualCutPlan) {
    columnList = columnList.filter(c => c.dataKey !== "balanceFabric");
  }
  if (!enablePerimeterUpdate) {
    columnList = columnList.filter(c => c.dataKey !== "perimeter");
  }
  if (orderFlow?.lotType === LotType.BULK) {
    // columnList = columnList.filter(c => !["shade", "width", "patternValue", "csv"].includes(c.dataKey));
    columnList = columnList.filter(c => !["shade", "width", "csv"].includes(c.dataKey));
  } else {
    columnList = columnList.filter(c => !["groupName", "balanceFabric"].includes(c.dataKey));
  }

  if (isEmpty(setting?.others) || !Array.isArray(setting.others) || !setting.others.includes(MiscType.CPL)) {
    columnList = columnList.filter(c => !["cplWarpShrPercent", "cplWeftShrPercent"].includes(c.dataKey));
  }

  columnList = columnList.map(c => ({ ...c, align: c.align || "center" }));

  let headerRows = [];
  if (Object.keys(groupRow)?.length > 1) {
    headerRows = [groupRow, columnList.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
  } else {
    headerRows = [columnList.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
  }
  return { columns: columnList, headerRows, sbList };
};

export const getRowsMarkerDetails = (order, orderFlow, planTabIdx, styleColor, showRatio = false, showTotal = true) => {
  if (isEmpty(order) || isEmpty(orderFlow?.markerList)) return [];

  const allowedQtyMap = !isEmpty(orderFlow?.orderConfig?.allowedQty) ? JSON.parse(orderFlow.orderConfig.allowedQty) : {};
  const setting = orderFlow?.orderConfig?.setting || {};

  let markerList =
    isTrue(orderFlow?.reGenerated) && planTabIdx === 0
      ? orderFlow.markerList.filter(m => m.markerType === MarkerType.FINAL || m.markerType === MarkerType.REMNANT_RETAINED)
      : orderFlow.markerList.filter(
          m => m.markerType === MarkerType.FINAL || m.markerType === MarkerType.MANUAL || m.markerType === MarkerType.REMNANT
        );

  const flowInfoList = getEffFlowInfoList(order, orderFlow);
  const sbMap = flowInfoList.flatMap(fi => fi.fpoSizeBreakupList || []).reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb }), {});
  const sbList = Object.values(sbMap).sort((a, b) => a.serialNo - b.serialNo);

  const sizeKeyList = sbList.sort((a, b) => a.serialNo - b.serialNo).map(({ sizeKey }) => sizeKey) || [];

  // {[GroupId]: {[styleColor]: fabric}} or {[GroupId]: fabric}
  let balanceFabricMap = markerList
    .filter(m => !isEmpty(m.group))
    .reduce((acc, m) => ({ ...acc, [m.group.id]: !isEmpty(m.group.fabric) ? JSON.parse(m.group.fabric) : {} }), {});
  if (isEmpty(styleColor)) {
    balanceFabricMap = Object.keys(balanceFabricMap).reduce(
      (acc, key) => ({
        ...acc,
        [key]: Object.values(balanceFabricMap[key]).reduce((acc, f) => acc + f, 0)
      }),
      {}
    );
  }
  // const isReplicateCutPlan =
  //   orderFlow?.part !== PART_SHELL && orderFlow?.orderConfig?.multiPartBundling === MULTI_PART_EXACT_SAME_BUNDLE;
  // const reGenerated = isTrue(orderFlow?.reGenerated);

  const orderQty = flowInfoList.flatMap(fi => fi.fpoSizeBreakupList || []).reduce((acc, sb) => acc + sb.qty, 0);

  let rowList = markerList
    .sort(cbMarkerSort)
    .map((m, idx) => {
      // let plies = 0;
      // let shellPlies;
      // let spreadPlies;
      // if (m.markerType === MarkerType.FINAL) {
      //   plies = m.markerPlyList
      //     .filter(mp => isEmpty(styleColor) || mp.styleColor === styleColor)
      //     .reduce((acc, mp) => acc + (planTabIdx === 1 ? mp?.actualPlies : mp?.plannedPlies), 0);
      //   // State: CUTPLAN_IN_PROGRESS = RePlan not done yet
      //   if (isReplicateCutPlan && !reGenerated && orderFlow.state === OState.CUTPLAN_IN_PROGRESS.state) {
      //     shellPlies = m.markerPlyList
      //       .filter(mp => isEmpty(styleColor) || mp.styleColor === styleColor)
      //       .reduce((acc, mp) => acc + mp.shellPlies, 0);
      //     plies = shellPlies;
      //   }
      // } else {
      //   plies = m.markerPlyList
      //     .filter(mp => isEmpty(styleColor) || mp.styleColor === styleColor)
      //     .reduce((acc, mp) => acc + mp?.plannedPlies, 0);

      //   if (reGenerated) {
      //     if (isReplicateCutPlan && planTabIdx === 1) {
      //       shellPlies = m.markerPlyList
      //         .filter(mp => isEmpty(styleColor) || mp.styleColor === styleColor)
      //         .reduce((acc, mp) => acc + mp.shellPlies, 0);
      //       spreadPlies = m.completed
      //         ? m.markerPlyList
      //             .filter(mp => isEmpty(styleColor) || mp.styleColor === styleColor)
      //             .reduce((acc, mp) => acc + mp.spreadPlies, 0)
      //         : null;
      //       if (orderFlow.state === OState.RE_PLANNED_REMNANT_CUTPLAN_IN_PROGRESS.state) {
      //         plies = shellPlies;
      //       }
      //     }
      //   } else {
      //     shellPlies = m.markerPlyList
      //       .filter(mp => isEmpty(styleColor) || mp.styleColor === styleColor)
      //       .reduce((acc, mp) => acc + mp.shellPlies, 0);
      //     spreadPlies = m.completed
      //       ? m.markerPlyList
      //           .filter(mp => isEmpty(styleColor) || mp.styleColor === styleColor)
      //           .reduce((acc, mp) => acc + mp.spreadPlies, 0)
      //       : null;
      //     if (orderFlow.state === OState.CUTPLAN_IN_PROGRESS.state) {
      //       plies = shellPlies;
      //     }
      //   }
      // }

      let plies = m.markerPlyList
        .filter(mp => isEmpty(styleColor) || mp.styleColor === styleColor)
        .reduce((acc, mp) => acc + (planTabIdx === 1 && m.markerType === MarkerType.FINAL ? mp?.actualPlies : mp?.plannedPlies), 0);
      let shellPlies;
      let spreadPlies;
      if (getPartType(orderFlow?.part) !== PART_SHELL && orderFlow?.orderConfig?.multiPartBundling === MULTI_PART_EXACT_SAME_BUNDLE) {
        shellPlies = m.markerPlyList.filter(mp => isEmpty(styleColor) || mp.styleColor === styleColor).reduce((acc, mp) => acc + mp.shellPlies, 0);
        spreadPlies = m.completed
          ? m.markerPlyList.filter(mp => isEmpty(styleColor) || mp.styleColor === styleColor).reduce((acc, mp) => acc + mp.spreadPlies, 0)
          : null;
        if (orderFlow.state === OState.CUTPLAN_IN_PROGRESS.state) {
          plies = shellPlies;
        }
      }

      const way = m.sizeRatioList.reduce((acc, sr) => acc + sr.ratio, 0);
      const qty = plies * way;
      const sizes = sizeKeyList.reduce((acc, sizeKey) => {
        const sizeRatio = m.sizeRatioList.find(sr => sr.sizeKey === sizeKey);
        const ratio = sizeRatio ? sizeRatio.ratio : 0;
        const q = showRatio ? ratio : ratio * plies;
        return { ...acc, [getDataKey(sizeKey)]: q === 0 && !showRatio ? "-" : q };
      }, {});

      const qtyPercent = Number(toFixed((qty * 100) / orderQty));
      const qtyPercentage = `${toFixed(qtyPercent)}%`;
      const fabricUsed = m.length
        ? SpreadingType.FACE_ONE_WAY !== orderFlow?.orderConfig?.spreadingType
          ? toFixed(m.layLength * plies * 2, 1)
          : toFixed(m.layLength * plies, 1)
        : 0;
      let balanceFabric = 0;
      const group = m.group || {};
      if (!isEmpty(m.group)) {
        if (isEmpty(styleColor)) {
          const availabelFabric = balanceFabricMap[m.group?.id] || 0;
          balanceFabric = availabelFabric - fabricUsed;
          balanceFabricMap[m.group?.id] = balanceFabric;
        } else {
          const availabelFabric = balanceFabricMap[m.group?.id][styleColor] || 0;
          balanceFabric = availabelFabric - fabricUsed;
          balanceFabricMap[m.group?.id][styleColor] = balanceFabric;
        }
      }

      const warpPattern =
        !isEmpty(group.warpPattern) && !isEmpty(group.weftPattern)
          ? toFixedSigned(group.warpPattern, 1)
          : !isEmpty(m.warpPattern)
            ? toFixedSigned(m.warpPattern, 1)
            : "";
      const weftPattern =
        !isEmpty(group.warpPattern) && !isEmpty(group.weftPattern)
          ? toFixedSigned(group.weftPattern, 1)
          : !isEmpty(m.weftPattern)
            ? toFixedSigned(m.weftPattern, 1)
            : "";
      const patternValue = !isEmpty(warpPattern) && !isEmpty(weftPattern) ? `L${warpPattern}%, W${weftPattern}%` : "";
      let width = isEmpty(group) ? m.width : toFixed(group.widthAxis, 1);
      const isCPL = !isEmpty(setting?.others) && Array.isArray(setting.others) && setting.others.includes(MiscType.CPL);
      if (isCPL && !isEmpty(setting?.cplWeftShrPercent) && !isEmpty(group.widthAxis)) {
        width = toFixed(group.widthAxis * 0.01 * (100 + setting?.cplWeftShrPercent), 1);
      }

      return {
        ...m,
        ...sizes,
        // markerNo: `M${m.markerNo || idx + 1}${m.markerType !== MarkerType.FINAL ? "*" : ""}`,
        mno: `M${m.markerNo || idx + 1}${m.markerType !== MarkerType.FINAL ? "*" : ""}`,
        plies,
        shellPlies,
        spreadPlies,
        qty,
        qtyPercent,
        qtyPercentage,
        way,
        layLength: isEmpty(m.layLength) ? "" : m.layLength.toFixed(2),
        efficiency: m.efficiency || "",
        cplWarpShrPercent: !isEmpty(m.layLength) && m.layLength > 0 ? m.cplWarpShrPercent : undefined,
        fabric: fabricUsed,
        balanceFabric: toFixed(balanceFabric, 1),
        width,
        patternValue: patternValue,
        csv: group?.csv || m.csv,
        groupName: getMarkerGroupName(m.group, setting),
        patternNumber: m.group?.patternNumber || m.patternNumber || ""
      };
    })
    .filter(e => e.plies > 0 || e.shellPlies > 0);

  const cutPlanGenerated =
    orderFlow.stateValue === OState.MAIN_CUTPLAN_GENERATED.stateValue ||
    (orderFlow.stateValue === OState.FINAL_GROUPING_COMPLETED.stateValue && isTrue(orderFlow.cutOptionsAvailable));

  //** Prepare Total Row **//
  const mapper = key => row =>
    typeof row[key] === "number" || REGEX_POSITIVE_DECIMAL.test(row[key]) || REGEX_POSITIVE_NUMBER.test(row[key]) ? parseFloat(row[key]) : 0;

  const keys = [...sizeKeyList.map(sk => getDataKey(sk)), "plies", "qty", "qtyPercent", "fabric"];
  const totalRow = keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]:
        key === "qtyPercent" || key === "fabric"
          ? rowList.map(mapper(key)).reduce((acc, value) => acc + value, 0)
          : Math.round(rowList.map(mapper(key)).reduce((acc, value) => acc + value, 0))
    }),
    {}
  );
  const totalShellPlies =
    rowList.filter(e => !isEmpty(e.shellPlies))?.length > 0
      ? rowList.filter(e => !isEmpty(e.shellPlies)).reduce((acc, e) => acc + e.shellPlies, 0)
      : null;
  const totalSpreadPlies =
    rowList.filter(e => !isEmpty(e.spreadPlies))?.length > 0
      ? rowList.filter(e => !isEmpty(e.spreadPlies)).reduce((acc, e) => acc + e.spreadPlies, 0)
      : null;
  const effMap = rowList.filter(row => row.efficiency).map(row => ({ key: row.efficiency, weight: row.plies * row.way }));
  const weightedEfficiency = getWeightedMean(effMap);
  const totalFabric = rowList.filter(row => row.layLength).reduce((acc, el) => acc + parseFloat(el.layLength) * el.plies, 0);
  const totalQty = rowList.filter(row => row.layLength && Number(row.layLength) > 0).reduce((acc, el) => acc + el.noOfWays * el.plies, 0);
  const layLength = totalQty > 0 ? toFixed(totalFabric / totalQty, 3) : "";
  if (rowList?.length > 1 && showTotal) {
    rowList.push({
      ...totalRow,
      shellPlies: totalShellPlies,
      spreadPlies: totalSpreadPlies,
      qtyPercentage: `${toFixed(totalRow.qtyPercent, 1)}%`,
      fabric: `${toFixed(totalRow.fabric, 1)}`,
      efficiency: weightedEfficiency > 0 ? toFixed(weightedEfficiency) : null,
      layLength,
      // name: "Total",
      // markerNo: cutPlanGenerated ? "Planned" : "Total",
      markerNo: cutPlanGenerated ? "Planned" : "Total",
      totalRow: true
    });
  }

  //** Prepare Balance Row **//
  if (cutPlanGenerated && !isEmpty(allowedQtyMap) && !showRatio) {
    let allowedQty = {};
    if (isEmpty(styleColor)) {
      allowedQty = Object.values(allowedQtyMap)
        .flatMap(aMap => Object.keys(aMap).map(sizeKey => ({ sizeKey, qty: aMap[sizeKey] })))
        .reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: (acc[sb.sizeKey] || 0) + sb.qty }), {});
    } else if (!isEmpty(allowedQtyMap[styleColor])) {
      allowedQty = Object.keys(allowedQtyMap[styleColor]).reduce((acc, sizeKey) => ({ ...acc, [sizeKey]: allowedQtyMap[styleColor][sizeKey] }), {});
    }

    const balanceQtyMap = sizeKeyList.reduce((acc, size) => ({ ...acc, [size]: (allowedQty[size] || 0) - (totalRow[size] || 0) }), {});

    const balanceQty = Object.values(balanceQtyMap).reduce((acc, qty) => acc + qty, 0);
    const qtyPercentage = `${toFixed((balanceQty * 100) / orderQty)}%`;

    rowList.push({
      ...balanceQtyMap,
      qty: balanceQty,
      qtyPercentage,
      markerNo: "Balance",
      // markerNo: "Balance",
      totalRow: true
    });
  }

  return rowList;
};

const UomElement = ({ column, record }) => (
  <span>
    {isEmpty(record[column?.dataKey]) ? "-" : toFixedValue(record[column?.dataKey], column.dataKey.endsWith("Cu") ? 3 : 2)}{" "}
    {!isEmpty(record[column?.dataKey]) ? UomCodeFromValue[record?.uom] : ""}
  </span>
);

export const getColumnsFabricUsageSummary = (orderFlow, planTabIdx, uom, onLinkClick) => {
  if (isEmpty(orderFlow)) return [];

  const isKnits = uom === UOM.KILOGRAM.value || uom === UOM.GRAM.value;

  let columns = [
    {
      dataKey: "style",
      title: "Style",
      align: "left",
      rSpan: isKnits ? 2 : 1,
      rowSpan: ({ record }) => (isKnits ? (record?.uom === UOM.KILOGRAM.value ? 2 : 0) : 1)
    },
    {
      dataKey: "color",
      title: "Style Color",
      align: "left",
      rSpan: isKnits ? 2 : 1,
      rowSpan: ({ record }) => (isKnits ? (record?.uom === UOM.KILOGRAM.value ? 2 : 0) : 1)
    },
    {
      dataKey: "itemCode",
      title: "Item Code",
      align: "left",
      rSpan: isKnits ? 2 : 1,
      rowSpan: ({ record }) => (isKnits ? (record?.uom === UOM.KILOGRAM.value ? 2 : 0) : 1),
      element: ({ column, record }) => (
        <Tooltip title={record[column.dataKey]}>
          <span style={{ color: typeof onLinkClick === "function" ? "blue" : undefined }} onClick={() => onLinkClick && onLinkClick(record)}>
            {truncate(record[column.dataKey], 20)}
          </span>
        </Tooltip>
      )
    },
    {
      dataKey: "fabColor",
      title: "Fabric Color",
      rSpan: isKnits ? 2 : 1,
      rowSpan: ({ record }) => (isKnits ? (record?.uom === UOM.KILOGRAM.value ? 2 : 0) : 1),
      element: ({ column, record }) => (
        <Tooltip title={record[column.dataKey]}>
          <span>{truncate(record[column.dataKey], 20)}</span>
        </Tooltip>
      )
    },
    {
      dataKey: "fabricPattern",
      title: "Fabric Pattern",
      rSpan: isKnits ? 2 : 1,
      rowSpan: ({ record }) => (isKnits ? (record?.uom === UOM.KILOGRAM.value ? 2 : 0) : 1)
    },
    {
      dataKey: "repeat",
      title: "Repeat",
      rSpan: isKnits ? 2 : 1,
      rowSpan: ({ record }) => (isKnits ? (record?.uom === UOM.KILOGRAM.value ? 2 : 0) : 1)
    },
    {
      dataKey: "rollForm",
      title: "Roll Form",
      rSpan: isKnits ? 2 : 1,
      rowSpan: ({ record }) => (isKnits ? (record?.uom === UOM.KILOGRAM.value ? 2 : 0) : 1)
    },
    {
      dataKey: "width",
      title: "Width",
      rSpan: isKnits ? 2 : 1,
      rowSpan: ({ record }) => (isKnits ? (record?.uom === UOM.KILOGRAM.value ? 2 : 0) : 1)
    },
    {
      dataKey: "gsm",
      title: "GSM",
      rSpan: isKnits ? 2 : 1,
      rowSpan: ({ record }) => (isKnits ? (record?.uom === UOM.KILOGRAM.value ? 2 : 0) : 1),
      element: ({ record, column }) => (isEmpty(record[column.dataKey]) ? "" : record[column.dataKey]),
      isFabCol: true
    },
    { dataKey: "allocated", title: "Alloc. Fab.", element: UomElement, isFabCol: true }
  ];
  if (orderFlow.styleList?.length <= 1) {
    columns = columns.filter(c => c.dataKey !== "style");
  }
  if (orderFlow.colorList?.length <= 1) {
    columns = columns.filter(c => c.dataKey !== "color");
  }

  if (orderFlow?.orderConfig?.multiPartBundling !== MULTI_PART_MANUAL_PLAN) {
    columns = columns.concat([
      { dataKey: "extraFab", title: "Extra Fab.", element: UomElement, isFabCol: true },
      { dataKey: "rejected", title: "Rejected", element: UomElement, isFabCol: true },
      { dataKey: "reserved", title: "Reserved", element: UomElement, isFabCol: true }
    ]);
  }

  const markers = orderFlow.markerList || [];
  if (planTabIdx === 0) {
    const tentative =
      markers
        .filter(m => (orderFlow.reGenerated ? m.markerType === MarkerType.REMNANT_RETAINED : m.markerType === MarkerType.REMNANT))
        .filter(m => m.completed).length === 0;
    if (orderFlow?.orderConfig?.multiPartBundling === MULTI_PART_MANUAL_PLAN) {
      columns = columns.concat([
        { dataKey: "planned", title: `Planned In Lay`, element: UomElement, isFabCol: true },
        { dataKey: "shortExtra", title: "Short/Extra", element: UomElement, isFabCol: true },
        { dataKey: "orderingCu", title: "BOM CU", element: UomElement, isFabCol: true },
        { dataKey: "layCu", title: "Lay CU", element: UomElement, isFabCol: true }
      ]);
    } else {
      columns = columns.concat([
        { dataKey: "tentPlanned", title: `Planned In Lay (Exp.)`, element: UomElement, isFabCol: true },
        { dataKey: "planned", title: `Planned In Lay`, element: UomElement, isFabCol: true },
        { dataKey: "tentEndBits", title: `EndBits (Exp.)`, element: UomElement, isFabCol: true },
        { dataKey: "endBits", title: `EndBits`, element: UomElement, isFabCol: true },
        { dataKey: "orderingCu", title: "BOM CU", element: UomElement, isFabCol: true },
        { dataKey: "layCu", title: "Lay CU", element: UomElement, isFabCol: true },
        { dataKey: "tentActualCu", title: `Plan CU (Exp.)`, element: UomElement, isFabCol: true },
        { dataKey: "actualCu", title: `Plan CU`, element: UomElement, isFabCol: true },
        { dataKey: "stdCu", title: `Std. CU`, element: UomElement, isFabCol: true }
      ]);

      if (tentative) {
        columns = columns.filter(c => !["planned", "endBits", "actualCu"].includes(c.dataKey));
      } else {
        columns = columns.filter(c => !["tentPlanned", "tentEndBits", "tentActualCu"].includes(c.dataKey));
      }
    }
  } else {
    const tentative = markers.filter(m => m.markerType === MarkerType.REMNANT).filter(m => m.completed).length === 0;
    columns = columns.concat([
      { dataKey: "shortageExcess", title: "Shortage/Excess", element: UomElement, isFabCol: true },
      { dataKey: "missing", title: "Missing", element: UomElement, isFabCol: true },
      { dataKey: "tentPlanned", title: `Planned In Lay (Exp.)`, element: UomElement, isFabCol: true },
      { dataKey: "planned", title: `Planned In Lay`, element: UomElement, isFabCol: true },
      { dataKey: "tentEndBits", title: `EndBits (Exp.)`, element: UomElement, isFabCol: true },
      { dataKey: "endBits", title: `EndBits`, element: UomElement, isFabCol: true },
      { dataKey: "orderingCu", title: "BOM CU", element: UomElement, isFabCol: true },
      { dataKey: "layCu", title: "Lay CU", element: UomElement, isFabCol: true },
      { dataKey: "tentActualCu", title: `Plan CU (Exp.)`, element: UomElement, isFabCol: true },
      { dataKey: "actualCu", title: `Plan CU`, element: UomElement, isFabCol: true },
      { dataKey: "stdCu", title: `Std. CU`, element: UomElement, isFabCol: true }
    ]);
    if (tentative) {
      columns = columns.filter(c => !["planned", "endBits", "actualCu"].includes(c.dataKey));
    } else {
      columns = columns.filter(c => !["tentPlanned", "tentEndBits", "tentActualCu"].includes(c.dataKey));
    }
  }

  return columns.map(c => ({ ...c, align: c.rowSpan ? "left" : "right" }));
};

export const getRowsFabricUsageSummary = (order, orderFlow, rollInfoList, ledgerList, fabricPatternList, uom, planTabIdx) => {
  if (isEmpty(order) || isEmpty(orderFlow) || orderFlow?.stateValue <= OState.FINAL_GROUPING_COMPLETED.stateValue) return [];

  const flowInfoList = getEffFlowInfoList(order, orderFlow);
  const extraPercentList = orderFlow?.orderConfig?.setting?.extraCutPercentList || [];

  let rows = flowInfoList.flatMap(flowInfo => {
    const part = flowInfo?.partList?.find(p => p.effPartName === orderFlow.part) || {};

    const ledger = !isEmpty(ledgerList) ? ledgerList.find(e => e.style === flowInfo.style && e.styleColor === flowInfo.color) : {};
    const fabric = ledger?.fabric || {};

    const fabricUsageList =
      planTabIdx === 0
        ? JSON.parse(orderFlow.orderConfig.fabricUsageInfo || "[]")
        : JSON.parse(orderFlow.orderConfig.reGeneratedFabricUsageInfo || "[]");

    const isKnits = uom === UOM.KILOGRAM.value || uom === UOM.GRAM.value;

    let fabricUsage = fabricUsageList.find(e => e.style === flowInfo.style && e.color === flowInfo.color && e.uom === UOM.METER.value) || {};
    let knitFabricUsage = fabricUsageList.find(e => e.style === flowInfo.style && e.color === flowInfo.color && e.uom === UOM.KILOGRAM.value) || {};

    let meanGsm, meanWidth;
    if (isKnits) {
      meanGsm = getWeightedMean(rollInfoList.map(ri => ({ key: ri.gsm, weight: ri.length })));
      meanWidth = getWeightedMean(rollInfoList.map(ri => ({ key: ri.effFinishedWidth || ri.effWidth, weight: ri.length })));
    }

    // Calculate Marker CU
    let markers = orderFlow?.markerList || [];
    if (orderFlow.reGenerated && planTabIdx === 0) {
      markers = markers.filter(m => m.markerType === MarkerType.FINAL || m.markerType === MarkerType.REMNANT_RETAINED);
    } else {
      markers = markers.filter(m => m.markerType === MarkerType.FINAL || m.markerType === MarkerType.REMNANT);
    }
    markers = markers
      .filter(m => !isEmpty(m.layLength) && m.layLength > 0)
      .map(m => ({ ...m, markerPly: m.markerPlyList?.find(mp => mp.styleColor === flowInfo.styleColor) || {} }))
      .map(m => ({
        ...m,
        plies: orderFlow.reGenerated && planTabIdx === 1 && m.markerType === MarkerType.FINAL ? m.markerPly.actualPlies : m.markerPly.plannedPlies
      }));
    const stdCuAllowance = orderFlow?.orderConfig?.stdCuAllowance || 0;
    let stdCu, stdCuInKg;
    if (stdCuAllowance > 0) {
      const fabricUsed = markers.reduce((acc, m) => acc + (m.layLength - m.endloss * 0.01) * (m.plies || 0), 0);
      const cutQty = markers.reduce((acc, m) => acc + m.noOfWays * (m.plies || 0), 0);
      const markerCu = cutQty > 0 ? toFixed(fabricUsed / cutQty, 3) : 0;
      stdCu = toFixedValue(markerCu * (1 + stdCuAllowance * 0.01), 3);

      if (isKnits) {
        stdCuInKg = toFixedValue(
          convert({ input: stdCu, uomIn: UOM.METER.value, uomOut: UOM.KILOGRAM.value, gsm: meanGsm, widthInCm: meanWidth }),
          3
        );
      }
    }

    // const fabric = fabrics[fabricId] || {};
    const extraRow = extraPercentList.find(e => e.styleColor === flowInfo.styleColor);
    const ocu = toFixedValue(part?.ocu, 3);
    const bomCuList = JSON.parse(orderFlow.bomCu || "[]");
    const bomCu = toFixedValue(bomCuList?.find(e => e.style === flowInfo.style && e.color === flowInfo.color)?.bomCu, 3);

    let extraPercent = !isEmpty(extraRow?.extra) ? extraRow?.extra : 0.0;
    let cutAll = extraRow?.cutAll || false;

    if (orderFlow?.orderConfig?.multiPartBundling === MULTI_PART_MANUAL_PLAN) {
      // Find Allocated Fabric //
      const allocatedLength = rollInfoList.reduce((acc, ri) => acc + ri.length, 0);

      // Find PlannedInLay, ShortageExcess and LayCu //
      const markerList = orderFlow?.markerList?.filter(m => m.markerType === MarkerType.MANUAL && !isEmpty(m.layLength)) || [];
      const plannedLength = markerList.reduce((acc, m) => {
        const plies = m.markerPlyList?.find(mp => mp.styleColor === flowInfo.styleColor)?.plannedPlies || 0;
        return acc + plies * m.layLength;
      }, 0);
      const plannedQty = markerList.reduce((acc, m) => {
        const plies = m.markerPlyList?.find(mp => mp.styleColor === flowInfo.styleColor)?.plannedPlies || 0;
        return acc + plies * m.noOfWays;
      }, 0);

      const shortageExcessLength = toFixed(allocatedLength - plannedLength, 1);

      fabricUsage = {
        ...fabricUsage,
        allocated: allocatedLength,
        planned: plannedLength,
        shortageExcess: shortageExcessLength,
        layCu: toFixedValue(plannedLength / plannedQty, 3),
        uom: UOM.METER.value
      };

      if (isKnits) {
        const allocatedWeight = rollInfoList.reduce((acc, ri) => acc + (ri.weight || 0), 0);
        const plannedWeight = convert({
          input: plannedLength,
          uomIn: UOM.METER.value,
          uomOut: UOM.KILOGRAM.value,
          gsm: meanGsm,
          widthInCm: meanWidth
        });
        const shortageExcessWeight = toFixed(allocatedWeight - plannedWeight, 1);
        knitFabricUsage = {
          ...knitFabricUsage,
          allocated: allocatedWeight,
          planned: plannedWeight,
          shortageExcess: shortageExcessWeight,
          layCu: toFixedValue(plannedWeight / plannedQty, 3),
          uom: UOM.KILOGRAM.value
        };
      }
    }

    const fabricPattern = Array.isArray(fabricPatternList) ? fabricPatternList.find(e => e.id === orderFlow?.orderConfig?.fabricPatternId) : {};

    const row = {
      ...flowInfo,
      parentId: null,
      extraPercent,
      cutAll,
      rollForm: rollInfoList[0]?.rollForm,
      productName: flowInfo.product?.name || "",
      effPartName: part?.effPartName || "",
      placement: part?.placement || "",
      fabricId: fabric.id,
      itemCode: fabric.itemCode,
      fabColor: ledger?.fabColor,
      fabricPattern: fabricPattern?.name,
      repeat: getRepeatSize(orderFlow?.orderConfig),
      width: `${ledger?.fabricItem?.orderingWidth || fabric?.width} ${UomCodeFromValue[ledger?.fabricItem?.uomWidth]}`,
      widthValue: ledger?.fabricItem?.orderingWidth || 0,
      gsm: `${ledger?.fabricItem?.orderingGsm || fabric?.gsm || ""}`,
      orderingCsv: ledger?.fabricItem?.orderingCsv,
      orderingWarpShrinkage: ledger?.fabricItem?.orderingWarpShrinkage || 0,
      orderingWeftShrinkage: ledger?.fabricItem?.orderingWeftShrinkage || 0,
      flowInfoList,
      orderFlowList: [orderFlow]
    };

    let rowList = [];
    if (isKnits) {
      rowList.push({
        ...row,
        ...knitFabricUsage,
        orderingCu: ocu,
        stdCu: stdCuInKg,
        tentPlanned: knitFabricUsage?.planned,
        tentEndBits: knitFabricUsage?.endBits,
        tentActualCu: knitFabricUsage?.actualCu,
        extraFab: knitFabricUsage?.extra
      });
    }
    rowList.push({
      ...row,
      ...fabricUsage,
      orderingCu: bomCu,
      stdCu,
      tentPlanned: fabricUsage?.planned,
      tentEndBits: fabricUsage?.endBits,
      tentActualCu: fabricUsage?.actualCu,
      extraFab: fabricUsage?.extra
    });

    return rowList;
  });
  return rows;
};

export const getColumnsCutSummary = (order, orderFlow, print = false) => {
  if (isEmpty(order) || isEmpty(orderFlow)) return [];

  const styleColorList = orderFlow.styleColorList;
  const styleCount = orderFlow.styleList?.length;

  const sizeBreakupList = findSizeBreakupList(orderFlow, order);
  const sizeGroups = getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup));
  const styleColorIdx = sizeGroups.reduce((acc, e, idx) => ({ ...acc, [e]: idx % 2 }), {});
  const groupedCutPlan = getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)))?.length > 1;

  let groupRow = {};
  let sizeGroup;
  const sizeList = sizeBreakupList.map((sb, idx) => {
    let leftBorder = false;
    if (sizeGroup !== sb.sizeGroup) {
      leftBorder = true;
      sizeGroup = sb.sizeGroup;
      groupRow[sb.sizeKey] = sb.sizeGroup;
    }
    return {
      dataKey: sb.sizeKey,
      title: sb.size,
      align: "center",
      styleColorIdx: sizeGroups.length > 1 ? styleColorIdx[sb.sizeGroup] : undefined,
      leftBorder,
      rightBorder: idx === sizeBreakupList.length - 1,
      colSpan: ({ column, row, rowIdx }) =>
        !groupedCutPlan || rowIdx === 1
          ? 1
          : isEmpty(row[column.dataKey])
            ? 0
            : getDistinctValues(sizeBreakupList.filter(sb => sb.sizeGroup === row[column.dataKey]).map(sb => sb.sizeKey))?.length
    };
  });

  const rowCount = orderFlow.lot + 2;
  let columns = [
    {
      dataKey: "styleColor",
      title: styleCount > 1 ? "Style" : "Color",
      rowSpan: ({ rowIdx }) => (rowIdx % rowCount === 0 ? rowCount : 0)
    },
    { dataKey: "label", title: "Label", align: "left" },
    ...sizeList,
    { dataKey: "total", title: "Total", leftBorder: true },
    { dataKey: "totalPercent", title: "Total(%)" }
  ];
  if (styleColorList.length === 1 || isFalse(print)) {
    columns = columns.filter(c => c.dataKey !== "styleColor");
  }

  columns = columns.map(c => ({ ...c, align: c.align || "center" }));

  let headerRows = [];
  if (Object.keys(groupRow)?.length > 1) {
    headerRows = [groupRow, columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
  } else {
    headerRows = [columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
  }

  return { columns, headerRows, sbList: sizeBreakupList };
};

export const getRowsCutSummary = (order, orderFlow, planTabIdx, styleColor, i18nMessages) => {
  if (isEmpty(order) || isEmpty(orderFlow) || orderFlow?.stateValue <= OState.FINAL_GROUPING_COMPLETED.stateValue) return [];

  const flowInfoList = getEffFlowInfoList(order, orderFlow);
  const sbMap = flowInfoList.flatMap(fi => fi.fpoSizeBreakupList || []).reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb }), {});
  const sizeBreakupList = Object.values(sbMap).sort((a, b) => a.serialNo - b.serialNo);
  const orderFlowList = getOrderFlowList(order, orderFlow);

  const styleColorList = orderFlow.styleColorList;
  const sizeKeyList = sizeBreakupList.map(({ sizeKey }) => sizeKey);
  const emptyQty = sizeKeyList.reduce((acc, sizeKey) => ({ ...acc, [sizeKey]: 0 }), {});

  const styleCount = listFromCsv(orderFlow?.styles).length;

  let rows = [];

  styleColorList
    .filter(sc => isEmpty(styleColor) || styleColor === sc)
    .forEach(sc => {
      const orderQty = flowInfoList
        .filter(fi => fi.styleColor === sc)
        .flatMap(fi => fi.sizeBreakupList || [])
        .filter(sb => sb.type === "ORDER")
        .reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: (acc[sb.sizeKey] || 0) + sb.qty }), emptyQty);
      let totalOrderQty = Object.keys(orderQty).reduce((acc, key) => acc + orderQty[key], 0);

      // Process Order Qty
      const fpoOrderQty = flowInfoList
        .filter(fi => fi.styleColor === sc)
        .flatMap(fi => fi.fpoSizeBreakupList || [])
        .reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: (acc[sb.sizeKey] || 0) + sb.qty }), emptyQty);
      let totalFpoOrderQty = Object.keys(fpoOrderQty).reduce((acc, key) => acc + fpoOrderQty[key], 0);

      const styleOrColor = styleCount > 1 ? sc.split(DEFAULT_SEPARATOR)[0] : sc.split(DEFAULT_SEPARATOR)[1];

      rows.push({
        ...orderQty,
        styleColor: styleOrColor,
        label: "Order Qty",
        total: totalOrderQty,
        totalPercent: "100%"
      });

      if (totalFpoOrderQty !== totalOrderQty) {
        rows.push({
          ...fpoOrderQty,
          styleColor: styleOrColor,
          label: lodashGet(i18nMessages || {}, `resources.orders.fields.fpoQty`, "FPO Qty"),
          total: totalFpoOrderQty,
          totalPercent: totalOrderQty > 0 ? `${toFixed((totalFpoOrderQty * 100) / totalOrderQty, 1)}%` : ""
        });
      }

      //** Process Cut Qty of Previous Lots **//
      let cutQtyPrevLots = {};
      const prevLotState = mapFromCsv(orderFlow.prevLotState, ";");
      const lotValues = Object.keys(prevLotState);
      const prevFlowList = orderFlowList
        .filter(
          of =>
            of.type === OrderFlowType.CUTPLAN &&
            orderFlow.productIdList?.every(id => of.productIdList?.includes(id)) &&
            of.styles === orderFlow.styles &&
            of.colors === orderFlow.colors
        )
        .filter(of => of.part === orderFlow.part && lotValues.includes(of.lotValue))
        .filter(of => isEmpty(orderFlow.inseams) || orderFlow.inseams === of.inseams)
        .filter(of => isEmpty(orderFlow.destinations) || orderFlow.destinations === of.destinations)
        .filter(of => isEmpty(orderFlow.deliveries) || orderFlow.deliveries === of.deliveries)
        // .filter(of => isEmpty(orderFlow.delModes) || orderFlow.delModes === of.delModes)
        .filter(of => isEmpty(orderFlow.flowRefs) || orderFlow.flowRefs === of.flowRefs)
        .filter(of => isEmpty(orderFlow.customerFlowRefs) || orderFlow.customerFlowRefs === of.customerFlowRefs)
        .sort((a, b) => a.plannedAt - b.plannedAt);

      for (let i = 0; i < prevFlowList.length; i++) {
        const prevOrderFlow = prevFlowList[i];
        let markers =
          prevOrderFlow?.markerList?.filter(
            m =>
              m.markerType === MarkerType.FINAL ||
              m.markerType === MarkerType.REMNANT ||
              m.markerType === MarkerType.MANUAL ||
              m.markerType === MarkerType.RE_CUTTING
          ) || [];
        const reCutGarmentQtyMap = JSON.parse(prevOrderFlow?.orderConfig?.reCutGarmentQty || "{}");
        const rejectedQtyMap = JSON.parse(prevOrderFlow?.orderConfig?.rejectedQty || "{}");
        const state = prevLotState[prevOrderFlow.lotValue] || STATE_PREV_LOT_CUTTING_NOT_COMPLETED;
        if (state === STATE_PREV_LOT_CUTTING_COMPLETED) {
          const cutQty = sizeKeyList.reduce((acc, sizeKey) => {
            const qty = markers
              .filter(m => m.sizeRatioList.find(sr => sr.sizeKey === sizeKey) !== undefined)
              .reduce((acc, m) => {
                const sizeRatio = m.sizeRatioList.find(sr => sr.sizeKey === sizeKey);
                const markerPly = m.markerPlyList.find(mp => mp.styleColor === sc);
                return acc + sizeRatio.ratio * markerPly.actualPlies;
              }, 0);
            return { ...acc, [sizeKey]: qty };
          }, {});
          sizeKeyList.forEach(sizeKey => {
            const reCutQty = (reCutGarmentQtyMap[sc] && reCutGarmentQtyMap[sc][sizeKey]) || 0;
            const rejectedQty = (rejectedQtyMap[sc] && rejectedQtyMap[sc][sizeKey]) || 0;
            cutQty[sizeKey] = cutQty[sizeKey] + reCutQty - rejectedQty;
          });
          // let total = Object.keys(cutQty).reduce((acc, key) => acc + cutQty[key], 0);
          // let totalPercent = `${toFixed((total * 100) / totalFpoOrderQty, 1)}%`;
          // rows.push({ ...cutQty, styleColor: styleOrColor, label: `Cut Qty, ${prevOrderFlow.lotValue}`, total, totalPercent });

          Object.keys(cutQty).forEach(key => {
            cutQtyPrevLots[key] = (cutQtyPrevLots[key] || 0) + cutQty[key];
          });
        } else {
          const approvedOrderCutQtyMap = JSON.parse(prevOrderFlow?.orderConfig?.approvedOrderCutQty || "{}");
          const approvedQty = approvedOrderCutQtyMap[sc] || {};
          // let total = Object.keys(approvedQty).reduce((acc, key) => acc + approvedQty[key], 0);
          // let totalPercent = `${toFixed((total * 100) / totalFpoOrderQty, 1)}%`;
          // rows.push({
          //   ...approvedQty,
          //   styleColor: styleOrColor,
          //   label: `Planned Qty, ${prevOrderFlow.lotValue}`,
          //   total,
          //   totalPercent
          // });

          Object.keys(approvedQty).forEach(key => {
            cutQtyPrevLots[key] = (cutQtyPrevLots[key] || 0) + approvedQty[key];
          });
        }
      }
      if (!isEmpty(cutQtyPrevLots)) {
        let total = Object.keys(cutQtyPrevLots).reduce((acc, key) => acc + cutQtyPrevLots[key], 0);
        let totalPercent = `${toFixed((total * 100) / totalOrderQty, 1)}%`;
        rows.push({
          ...cutQtyPrevLots,
          styleColor: styleOrColor,
          label: `Already Planned/Cut Qty`,
          total,
          totalPercent
        });
      }

      // Process Cut Qty of Current Lot
      let markers = orderFlow.markerList;
      if (orderFlow.reGenerated && planTabIdx === 0) {
        markers = markers.filter(m => m.markerType === MarkerType.FINAL || m.markerType === MarkerType.REMNANT_RETAINED);
      } else {
        markers = markers.filter(m => m.markerType === MarkerType.FINAL || m.markerType === MarkerType.MANUAL || m.markerType === MarkerType.REMNANT);
      }

      const cutQty = sizeKeyList.reduce((acc, sizeKey) => {
        const qty = markers
          .filter(m => m.sizeRatioList.find(sr => sr.sizeKey === sizeKey) !== undefined)
          .reduce((acc, m) => {
            const sizeRatio = m.sizeRatioList.find(sr => sr.sizeKey === sizeKey);
            const markerPly = m.markerPlyList.find(mp => mp.styleColor === sc);
            let plies = planTabIdx === 1 && m.markerType === MarkerType.FINAL ? markerPly?.actualPlies : markerPly?.plannedPlies;
            if (
              getPartType(orderFlow?.part) !== PART_SHELL &&
              orderFlow?.orderConfig?.multiPartBundling === MULTI_PART_EXACT_SAME_BUNDLE &&
              orderFlow.state === OState.CUTPLAN_IN_PROGRESS.state
            ) {
              plies = markerPly?.shellPlies || plies;
            }

            return acc + sizeRatio.ratio * plies;
          }, 0);
        return { ...acc, [sizeKey]: qty };
      }, {});
      let total = Object.keys(cutQty).reduce((acc, key) => acc + cutQty[key], 0);
      let totalPercent = `${toFixed((total * 100) / totalOrderQty, 1)}%`;
      rows.push({
        ...cutQty,
        styleColor: styleOrColor,
        label: `Planned Qty${!isEmpty(lotValues) ? `, ${orderFlow.lotValue}` : ""}`,
        total,
        totalPercent
      });

      if (!isEmpty(lotValues)) {
        let totalQty = sizeKeyList.reduce((acc, size) => ({ ...acc, [size]: cutQty[size] + (cutQtyPrevLots[size] || 0) }), {});
        let total = Object.keys(totalQty).reduce((acc, key) => acc + totalQty[key], 0);
        totalPercent = `${toFixed((total * 100) / totalFpoOrderQty, 1)}%`;
        rows.push({ ...totalQty, styleColor: styleOrColor, total, totalPercent, label: "Total" });
      }

      // Process Balance Qty w.r.t FPO Qty
      let totalBalance = 0;
      let balanceQty = sizeKeyList.reduce((acc, size) => {
        const balance = cutQty[size] + (cutQtyPrevLots[size] || 0) - fpoOrderQty[size];
        totalBalance += balance;
        return {
          ...acc,
          [size]: fpoOrderQty[size] > 0 ? `${signedValue(balance)}(${toFixedSigned((balance * 100) / fpoOrderQty[size], 1)}%)` : "0(0.0%)"
        };
      }, {});
      totalPercent = `${toFixedSigned((totalBalance * 100) / totalFpoOrderQty, 1)}%`;
      if (totalFpoOrderQty !== totalOrderQty) {
        rows.push({
          ...balanceQty,
          styleColor: styleOrColor,
          total: totalBalance,
          totalPercent,
          label: "Short/Extra w.r.t FPO Qty"
        });
      }

      // Process Balance Qty w.r.t Order Qty
      totalBalance = 0;
      balanceQty = sizeKeyList.reduce((acc, size) => {
        const balance = cutQty[size] + (cutQtyPrevLots[size] || 0) - orderQty[size];
        totalBalance += balance;
        return {
          ...acc,
          [size]: orderQty[size] > 0 ? `${signedValue(balance)}(${toFixedSigned((balance * 100) / orderQty[size], 1)}%)` : `0(0.0%)`
        };
      }, {});
      totalPercent = `${toFixedSigned((totalBalance * 100) / totalOrderQty, 1)}%`;
      rows.push({
        ...balanceQty,
        styleColor: styleOrColor,
        total: totalBalance,
        totalPercent,
        label: totalFpoOrderQty !== totalOrderQty ? "Short/Extra w.r.t Order Qty" : "Short/Extra"
      });
    });

  return rows;
};

export const getColumnsShadeSummary = (order, orderFlow, print = false) => {
  if (isEmpty(order) || isEmpty(orderFlow)) return [];

  const styleColorList = orderFlow.styleColorList;

  const sbList = findSizeBreakupList(orderFlow, order);
  const sizeGroups = getDistinctValues(sbList.sort((a, b) => a.serialNo - b.serialNo).map(sb => sb.sizeGroup));
  const styleColorIndex = sizeGroups.reduce((acc, e, idx) => ({ ...acc, [e]: idx % 2 }), {});
  const groupedCutPlan = sizeGroups?.length > 1;

  let sizeGroup;
  let groupRow = {};
  const sizeList = sbList.map((sb, idx) => {
    let leftBorder = false;
    if (sizeGroup !== sb.sizeGroup) {
      leftBorder = true;
      sizeGroup = sb.sizeGroup;
      groupRow[sb.sizeKey] = sizeGroup;
    }
    return {
      dataKey: sb.sizeKey,
      title: sb.size,
      align: "center",
      styleColorIdx: sizeGroups.length > 1 ? styleColorIndex[sb.sizeGroup] : undefined,
      leftBorder,
      rightBorder: idx === sbList.length - 1,
      colSpan: ({ column, row, rowIdx }) =>
        !groupedCutPlan || rowIdx === 1
          ? 1
          : isEmpty(row[column.dataKey])
            ? 0
            : getDistinctValues(sbList.filter(sb => sb.sizeGroup === row[column.dataKey]).map(sb => sb.sizeKey))?.length
    };
  });

  const styleCount = orderFlow?.styleList?.length;

  let rowCount = 1;
  if (styleColorList.length > 1) {
    const layMap = JSON.parse((orderFlow.orderConfig && orderFlow.orderConfig?.laySlip) || "{}");
    const layList = Object.keys(layMap).flatMap(key => layMap[key]);
    let rowList = layList.flatMap(
      lay =>
        lay &&
        lay.rowList &&
        lay.rowList.map(row => ({
          ...row,
          layType: lay.layType,
          markerId: lay.marker.id
        }))
    );
    const shadeList = getDistinctValues(rowList.map(row => row.shade).filter(shade => shade));
    rowCount = shadeList.length + 1;
  }

  let columns = [
    {
      dataKey: "styleColor",
      title: styleCount > 1 ? "Style" : "Color",
      rowSpan: ({ rowIdx }) => (rowIdx % rowCount === 0 ? rowCount : 0)
    },
    { dataKey: "shade", title: "Shade", align: "center" },
    ...sizeList,
    {
      dataKey: "total",
      title: "Total",
      align: "center",
      leftBorder: true
    }
  ];
  if (styleColorList.length === 1 || !isTrue(print)) {
    columns = columns.filter(c => c.dataKey !== "styleColor");
  }

  columns = columns.map(c => ({ ...c, align: "center" }));

  let headerRows = [];
  if (Object.keys(groupRow)?.length > 1) {
    headerRows = [groupRow, columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
  } else {
    headerRows = [columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
  }
  return { columns, headerRows, sbList };
};

export const getRowsShadeSummary = (order, orderFlow, planTabIdx, styleColor) => {
  if (isEmpty(order) || isEmpty(orderFlow) || orderFlow?.stateValue <= OState.FINAL_GROUPING_COMPLETED.stateValue) return [];

  const styleColorList = orderFlow.styleColorList;
  const flowInfoList = getCutOrderFlowInfoList(order, orderFlow);
  const sbMap = flowInfoList.flatMap(fi => fi.fpoSizeBreakupList || []).reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb }), {});
  const sizeBreakupList = Object.values(sbMap).sort((a, b) => a.serialNo - b.serialNo);
  const styleCount = orderFlow?.styleList?.length;

  const sizeKeyList = sizeBreakupList.map(({ sizeKey }) => sizeKey);
  const markerMap = orderFlow.markerList.reduce((acc, m) => ({ ...acc, [m.id]: m }), {});
  let layList = [];
  if (planTabIdx === 0) {
    const layMap = JSON.parse(orderFlow.orderConfig?.laySlip || "{}");
    layList = layMap && Object.keys(layMap).flatMap(key => layMap[key]);
  } else if (orderFlow.reGenerated && planTabIdx === 1) {
    layList = JSON.parse(orderFlow.orderConfig?.feedbackLaySlip || "{}");
    if (!isEmpty(orderFlow.orderConfig?.reGeneratedLaySlip)) {
      layList = layList.concat(JSON.parse(orderFlow.orderConfig.reGeneratedLaySlip));
    }
  }
  if (isEmpty(layList)) return [];

  let rowList = layList.flatMap(
    lay =>
      lay.rowList?.map(row => ({
        ...row,
        layType: lay.layType,
        markerId: lay.marker.id
      })) || []
  );
  const shadeList = getDistinctValues(rowList.map(row => row.shade).filter(shade => shade));

  const approvedOrderCutQtyMap = JSON.parse(orderFlow.orderConfig.approvedOrderCutQty || "{}");

  let rows = [];
  styleColorList
    .filter(sc => isEmpty(styleColor) || sc === styleColor)
    .forEach(sc => {
      let approvedQty = approvedOrderCutQtyMap[sc] || {};
      approvedQty = Object.keys(approvedQty).reduce((acc, sKey) => ({ ...acc, [sKey]: approvedQty[sKey] }), {});
      const overallTotal = Object.keys(approvedQty).reduce((acc, key) => acc + approvedQty[key], 0);
      const styleOrColor = styleCount > 1 ? sc.split(DEFAULT_SEPARATOR)[0] : sc.split(DEFAULT_SEPARATOR)[1];

      let shadeWiseData = shadeList
        .map(shade => {
          let sum = 0;
          const cutQty = sizeKeyList.reduce((acc, sKey) => {
            const rows = rowList.filter(row => row.styleColor === sc && row.shade === shade);
            const qty = rows.reduce((acc, row) => {
              const sizeRatio = markerMap[row.markerId] && markerMap[row.markerId].sizeRatioList.find(sr => sr.sizeKey === sKey);
              const ratio = sizeRatio ? sizeRatio.ratio : 0;
              const plies = row.actualPly;
              return acc + ratio * plies;
            }, 0);
            sum += qty;
            return { ...acc, [sKey]: qty };
          }, {});
          return { ...cutQty, styleColor: styleOrColor, shade, total: sum, shadeCount: shadeList.length };
        })
        .filter(e => e.total > 0);

      const totalRow = sizeKeyList.reduce(
        (acc, sKey) => ({
          ...acc,
          [sKey]: shadeWiseData.reduce((acc, row) => acc + row[sKey], 0)
        }),
        {}
      );
      const total = Object.keys(totalRow).reduce((acc, key) => acc + totalRow[key], 0);
      shadeWiseData = shadeWiseData.map(({ styleColor, shade, total, shadeCount, ...cutQty }) => ({
        styleColor,
        shade,
        shadeCount,
        ...Object.keys(cutQty).reduce(
          (acc, size) => ({
            ...acc,
            [size]: `${cutQty[size]} (${approvedQty[size] > 0 ? toFixed((cutQty[size] * 100) / approvedQty[size], 0) : "0"}%)`
          }),
          {}
        ),
        total: `${total} (${overallTotal > 0 ? toFixed((total * 100) / overallTotal, 0) : "0"}%)`
      }));
      rows = [...rows, ...shadeWiseData, { ...totalRow, styleColor: styleOrColor, shade: "Total", total }];
    });

  return rows;
};

export const printCutPlan = (
  order,
  orderFlow,
  uom,
  planTabIdx,
  rollInfoList,
  ledgerList,
  logs,
  orderFields = [],
  i18nMessages = {},
  tenant,
  fabricPatternList = null,
  unit = {},
  alertParameterList = [],
  enablePerimeterUpdate = false
) => {
  if (isEmpty(order) || isEmpty(orderFlow.markerList)) return;

  // const logList = logs.filter(log => log.flowId === orderFlow?.id);

  // const sentForApprovalAt = logList.filter(log => log.event === "Cut Plan sent for Approval").sort((a, b) => b.createdAt - a.createdAt)[0]?.createdAt;
  // const approvedAt = logList.filter(log => log.event === "Cut Plan Approved").sort((a, b) => b.createdAt - a.createdAt)[0]?.createdAt;

  const manualCutPlan = orderFlow?.orderConfig?.multiPartBundling === MULTI_PART_MANUAL_PLAN;
  const sizeBreakupList = findSizeBreakupList(orderFlow, order);

  const hasWeight = rollInfoList?.some(r => !isEmpty(r.weight));
  const wash = orderFlow?.groupList?.some(g => !isEmpty(g.warpPattern));
  const alertReasons = orderFlow.orderConfig.alerts ? JSON.parse(orderFlow.orderConfig.alerts) : [];

  let finalParameters = choicesParameter;
  if (!hasWeight) {
    finalParameters = finalParameters?.filter(p => !p.name?.includes("GSM"));
  }
  if (!wash) {
    finalParameters = finalParameters?.filter(p => !p.name?.includes("Shrinkage"));
  }

  const varianceAnalysisInfo = JSON.parse(orderFlow?.orderConfig?.varianceAnalysisInfo || "[]") || [];
  const varianceAnalysis = varianceAnalysisInfo[0] || {};

  let rows = finalParameters.map((parameter, idx) => {
    const alertParameter = Array.isArray(alertParameterList) ? alertParameterList?.find(ap => ap.parameter === parameter.id) || {} : {};
    const config = PARAMETER_CONFIG[parameter.id];

    const reasons = alertReasons?.find(ar => ar.parameter === parameter?.id)?.reasons?.join(",");

    if (config) {
      const values = config.getValues(varianceAnalysis, alertParameterList, orderFlow);
      return {
        ...alertParameter,
        ...values,
        serialNo: idx + 1,
        parameterName: parameter.name,
        reasons: reasons || ""
      };
    }

    return {
      ...alertParameter,
      serialNo: idx + 1,
      parameterName: parameter.name,
      reasons: reasons || ""
    };
  });

  const sizeCharacterCount = sizeBreakupList.reduce((acc, sb) => acc + maxCharacterCount(sb.size), 0);
  const charactersPerPage = 50;
  let pageFormat = "a4";
  if (Math.ceil(sizeCharacterCount / charactersPerPage) === 1) {
    pageFormat = "a4";
  } else if (Math.ceil(sizeCharacterCount / charactersPerPage) === 2) {
    pageFormat = "a3";
  } else if (Math.ceil(sizeCharacterCount / charactersPerPage) === 3) {
    pageFormat = "a2";
  } else {
    pageFormat = "a1";
  }
  var doc = new jsPDF("l", "mm", pageFormat);

  const markers = orderFlow.markerList;
  let reportName = "Cut Order Plan";
  let filename;
  if (planTabIdx === 0) {
    const tentative =
      markers
        .filter(m => (orderFlow.reGenerated ? m.markerType === MarkerType.REMNANT_RETAINED : m.markerType === MarkerType.REMNANT))
        .filter(m => m.completed).length === 0;
    if (tentative) {
      filename = "TENTATIVE CUTPLAN " + order.customer.name + " " + order.poRef + ".pdf";
      reportName = "Tentative Cut Order Plan";
    } else {
      filename = "CUTPLAN " + order.customer.name + " " + order.poRef + ".pdf";
      reportName = "Cut Order Plan";
    }
  } else {
    filename = "CUTPLAN RE " + order.customer.name + " " + order.poRef + ".pdf";
    reportName = "Cut Order Plan (Re-Planned)";
  }
  setDefault(doc, reportName, tenant, null, unit);
  doc.setFontSize(10);
  const pageHeight = doc.internal.pageSize.getHeight();

  const orderInfo = getOrderInfo(order, orderFlow);
  let columns = orderInfo.columns.map(c => ({ ...c, header: lodashGet(i18nMessages, `resources.orders.fields.${c.dataKey}`, c.header) }));

  let startY = 24;
  const tableWidth = doc.internal.pageSize.getWidth() - 14;

  var autoTableOptions = {
    startY: startY + 10,
    body: orderInfo.body,
    columns
  };

  ////// Print Order Info //////////////
  doc.text("Order Details", 7, startY + 7);
  addTable(doc, autoTableOptions, { tableWidth });

  ////// Print Fabric Info //////////////
  columns = getColumnsFabricUsageSummary(orderFlow, planTabIdx, uom);
  let body = getRowsFabricUsageSummary(order, orderFlow, rollInfoList, ledgerList, fabricPatternList, uom, planTabIdx);
  columns = columns.map(c => ({ ...c, header: lodashGet(i18nMessages, `resources.cutPlan.fields.flowInfo.${c.dataKey}`, c.title) }));

  startY = doc.autoTable.previous.finalY;

  autoTableOptions = {
    startY: startY + 10,
    head: [columns.map(c => c.header)],
    body: body.map((row, idx) =>
      columns.flatMap(c =>
        !isEmpty(c.rSpan) && c.rSpan > 1
          ? idx % 2 === 0
            ? [{ rowSpan: c.rSpan, content: row[c.dataKey] || "", styles: { valign: "middle" } }]
            : []
          : !isEmpty(row[c.dataKey])
            ? [`${toFixedValue(row[c.dataKey], c.dataKey.endsWith("Cu") ? 3 : 2)}${isTrue(c.isFabCol) ? ` ${UomCodeFromValue[row.uom]}` : ""}`]
            : ["-"]
      )
    )
  };

  doc.text("Fabric Usage Summary", 7, startY + 7);
  addTable(doc, autoTableOptions, { tableWidth, minCharLen: 18 });

  ////// Print Cutplan Summary //////////////
  const colCutSummary = getColumnsCutSummary(order, orderFlow, true);
  body = getRowsCutSummary(order, orderFlow, planTabIdx);
  columns = colCutSummary.columns.map(c => ({ ...c, header: c.title }));
  let head = [];
  if (colCutSummary.headerRows?.length === 2) {
    const sizgeGroupRow = columns
      .map(c =>
        ["label", "styleColor", "total", "totalPercent"].includes(c.dataKey)
          ? ""
          : isEmpty(colCutSummary.headerRows[0][c.dataKey])
            ? undefined
            : {
                content: colCutSummary.headerRows[0][c.dataKey],
                colSpan: getDistinctValues(
                  colCutSummary.sbList.filter(sb => sb.sizeGroup === colCutSummary.headerRows[0][c.dataKey]).map(sb => sb.sizeKey)
                )?.length,
                styles: { halign: "center" }
              }
      )
      .filter(e => e !== undefined);
    head.push(sizgeGroupRow);
    head.push(columns.map(c => colCutSummary.headerRows[1][c.dataKey]));
  } else {
    head.push(columns.map(c => colCutSummary.headerRows[0][c.dataKey]));
  }

  body = body.map(row => columns.map(c => row[c.dataKey]));

  startY = doc.autoTable.previous.finalY;
  if (pageHeight - startY < minReqHeight) {
    doc.addPage();
    startY = 10;
  }

  autoTableOptions = {
    startY: startY + 10,
    head,
    body,
    headStyles: { halign: "center" },
    bodyStyles: { halign: "center" }
  };

  doc.text("Cut Plan Summary", 7, startY + 7);
  addTable(doc, autoTableOptions, { tableWidth });

  ////// Print Cutplan //////////////
  const colMarkers = getColumnsMarkerDetails(order, orderFlow, enablePerimeterUpdate);
  body = getRowsMarkerDetails(order, orderFlow, planTabIdx);
  columns = colMarkers.columns.map(c => ({ ...c, header: c.title }));
  const sizeKeys = colMarkers?.sbList.map(e => e?.sizeKey);
  head = [];
  if (colMarkers.headerRows?.length === 2) {
    const sizgeGroupRow = columns
      .map(c =>
        !sizeKeys.includes(c?.dataKey)
          ? ""
          : isEmpty(colMarkers.headerRows[0][c.dataKey])
            ? undefined
            : {
                content: colMarkers.headerRows[0][c.dataKey],
                colSpan: getDistinctValues(colMarkers.sbList.filter(sb => sb.sizeGroup === colMarkers.headerRows[0][c.dataKey]).map(sb => sb.sizeKey))
                  ?.length,
                styles: { halign: "center" }
              }
      )
      .filter(e => e !== undefined);
    head.push(sizgeGroupRow);
    head.push(columns.map(c => colMarkers.headerRows[1][c.dataKey]));
  } else {
    head.push(columns.map(c => colMarkers.headerRows[0][c.dataKey]));
  }

  startY = doc.autoTable.previous.finalY;
  if (pageHeight - startY < minReqHeight) {
    doc.addPage();
    startY = 10;
  }

  body = body.map(row => columns.map(c => (c.dataKey === "markerNo" ? row.mno : row[c.dataKey])));

  autoTableOptions = {
    startY: startY + 10,
    head,
    body,
    headStyles: { halign: "center" },
    bodyStyles: { halign: "center" }
  };

  doc.text("Cut Plan", 7, startY + 7);
  addTable(doc, autoTableOptions, { tableWidth, minWidth: 10 });

  ////// Print Highlights //////////////
  startY = doc.autoTable.previous.finalY;
  if (pageHeight - startY < minReqHeight) {
    doc.addPage();
    startY = 10;
  }

  head = [["Parameter", "Booking/Target", "Actual", "Difference", "Reason"]];
  body = [
    ...rows.map(r => [r.parameterName, r.booking, r.actual, r.diff, r.reasons]),
    [
      "CAD Remarks",
      "",
      "",
      "",
      orderFlow.orderConfig.cadRemarks || ""
      // sentForApprovalAt ? format(new Date(sentForApprovalAt)).format("dd MM yy, hh:mm a") : ""
    ],
    [
      "Merchant Remarks",
      "",
      "",
      "",
      `${orderFlow.orderConfig.cutPlanApproval === "Approved" ? orderFlow.orderConfig.merchantRemarks || "Approved" : "Not Approved"}`
      // approvedAt && orderFlow.orderConfig.cutPlanApproval === "Approved" ? format(new Date(approvedAt)).format("dd MM yy, hh:mm a") : ""
    ]
  ];

  doc.text("Variance Report", 7, startY + 7);
  doc.autoTable({ startY: startY + 10, head, body });

  //// Print Shade Summary //////////////
  if (!manualCutPlan) {
    const colShadeSummary = getColumnsShadeSummary(order, orderFlow, true);
    body = getRowsShadeSummary(order, orderFlow, planTabIdx);
    columns = colShadeSummary.columns.map(c => ({ ...c, header: c.title }));
    head = [];
    if (colShadeSummary.headerRows?.length === 2) {
      const sizgeGroupRow = columns
        .map(c =>
          ["shade", "total"].includes(c.dataKey)
            ? ""
            : isEmpty(colShadeSummary.headerRows[0][c.dataKey])
              ? undefined
              : {
                  content: colShadeSummary.headerRows[0][c.dataKey],
                  colSpan: getDistinctValues(
                    colShadeSummary.sbList.filter(sb => sb.sizeGroup === colShadeSummary.headerRows[0][c.dataKey]).map(sb => sb.sizeKey)
                  )?.length,
                  styles: { halign: "center" }
                }
        )
        .filter(e => e !== undefined);
      head.push(sizgeGroupRow);
      head.push(columns.map(c => colShadeSummary.headerRows[1][c.dataKey]));
    } else {
      head.push(columns.map(c => colShadeSummary.headerRows[0][c.dataKey]));
    }
    body = body.map(row => columns.map(c => row[c.dataKey]));

    startY = doc.autoTable.previous.finalY;
    if (pageHeight - startY < minReqHeight) {
      doc.addPage();
      startY = 10;
    }

    autoTableOptions = {
      startY: startY + 10,
      head,
      body,
      bodyStyles: { halign: "center" }
    };

    doc.text("Shade Summary", 7, startY + 7);
    addTable(doc, autoTableOptions, { tableWidth, minWidth: 15 });
  }

  ////// Print Cut Plan Setting //////////////
  startY = doc.autoTable.previous.finalY;
  if (pageHeight - startY < minReqHeight) {
    doc.addPage();
    startY = 10;
  }
  const settingInfo = getSettingInfo(orderFlow);
  columns = settingInfo.columns.map(c => ({ ...c, header: lodashGet(i18nMessages, `resources.orders.fields.${c.dataKey}`, c.header) }));

  autoTableOptions = {
    body: settingInfo?.body,
    columns,
    startY: startY + 10
  };

  doc.text("Cut Plan Setting", 7, startY + 7);
  addTable(doc, autoTableOptions, { minWidth: 15, tableWidth });

  // // For Multi Color, Print Cut Plan Color wise
  // if (styleColorList.length > 1) {
  //   doc.addPage();
  //   startY = 10;

  //   doc.setFontSize(12);
  //   doc.text(`CUT PLANS ${styleCount > 1 ? "STYLE" : "COLOR"} WISE`, doc.internal.pageSize.getWidth() / 2, startY + 10, "center");
  //   startY = startY + 10;
  //   doc.setFontSize(10);

  //   styleColorList.forEach((styleColor, idx) => {
  //     header = getColumnsMarkerDetails(order, orderFlow);
  //     data = getRowsMarkerDetails(order, orderFlow, planTabIdx, styleColor);

  //     data = data.filter(row => row.qty > 0);
  //     if (idx !== 0) {
  //       startY = doc.autoTable.previous.finalY;
  //     }
  //     if (pageHeight - startY < minReqHeight) {
  //       doc.addPage();
  //       startY = 10;
  //     }
  //     const styleOrColor = styleCount > 1 ? styleColor.split(DEFAULT_SEPARATOR)[0] : styleColor.split(DEFAULT_SEPARATOR)[1];

  //     doc.text("Cut Plan: " + styleOrColor, 7, startY + 7);
  //     doc.autoTable(header, data, { startY: startY + 10 });
  //   });
  // }

  doc.putTotalPages(totalPagesExp);
  doc.save(filename);
};

const maxCharacterCount = size => {
  if (!size) return 0;
  const splits = size.split(" ");
  let max = 0;
  for (let i = 0; i < splits.length; i++) {
    if (splits[i].length > max) {
      max = splits[i].length;
    }
  }
  return max;
};

const getOrderInfo = (order, cutOrder) => {
  if (isEmpty(order?.flowInfoList) || isEmpty(order)) return { columns: [], body: [] };

  const flowInfoList = getCutOrderFlowInfoList(order, cutOrder);

  let columnList = [
    { dataKey: "poRef", header: "PO Ref" },
    { dataKey: "customerName", header: "Customer" },
    { dataKey: "productName", header: "Product" },
    { dataKey: "style", header: "Style Name" },
    { dataKey: "styleNo", header: "Style No" },
    { dataKey: "fit", header: "Fit" },
    { dataKey: "color", header: "Style Color" },
    { dataKey: "partName", header: "Part" },
    { dataKey: "lotValue", header: "Lot" },
    { dataKey: "inseam", header: "Inseam" },
    { dataKey: "destination", header: "Destination" },
    { dataKey: "deliveryDate", header: "Del. Date" },
    { dataKey: "flowRef", header: "Line ID" },
    { dataKey: "customerFlowRef", header: "BPO" },
    { dataKey: "orderQty", header: "Order Qty" },
    { dataKey: "extra", header: "Extra(%)" }
  ];

  //** Exclude Fields if value is empty in each flowInfo **//

  if (flowInfoList.every(fi => isEmpty(fi.styleNo))) {
    columnList = columnList.filter(c => c.dataKey !== "styleNo");
  }
  if (flowInfoList.every(fi => isEmpty(fi.fit))) {
    columnList = columnList.filter(c => c.dataKey !== "fit");
  }
  if (flowInfoList.every(fi => isEmpty(fi.inseam))) {
    columnList = columnList.filter(c => c.dataKey !== "inseam");
  }
  if (flowInfoList.every(fi => isEmpty(fi.destination))) {
    columnList = columnList.filter(c => c.dataKey !== "destination");
  }
  if (flowInfoList.every(fi => isEmpty(fi.deliveryDate))) {
    columnList = columnList.filter(c => c.dataKey !== "deliveryDate");
  }
  if (flowInfoList.every(fi => isEmpty(fi.flowRef))) {
    columnList = columnList.filter(c => c.dataKey !== "flowRef");
  }
  if (flowInfoList.every(fi => isEmpty(fi.customerFlowRef))) {
    columnList = columnList.filter(c => c.dataKey !== "customerFlowRef");
  }

  const body = flowInfoList.map(fi => ({
    poRef: order.poRef,
    customerName: order.customer?.name,
    seasonName: order.season?.name,
    productName: fi.product?.name,
    style: fi.style,
    styleNo: fi.styleNo,
    color: fi.color,
    partName: cutOrder.part,
    lotValue: cutOrder.lotValue,
    inseam: fi.inseam,
    destination: fi.destination,
    deliveryDate: fi.deliveryDate,
    flowRef: fi.flowRef,
    customerFlowRef: fi.customerFlowRef,
    orderQty: fi.orderQty,
    extra: fi.extra
  }));

  return { columns: columnList, body };
};

const getSettingInfo = cutOrder => {
  //** Prepare Column List **//
  let columnList = [
    { dataKey: "maxWay", header: "Max. garment per Ply" },
    { dataKey: "fabricDirection", header: "Fabric Direction" },
    { dataKey: "discardedWays", header: "Discarded Ways" },
    { dataKey: "reservation", header: "Panel Repl. Reservation" },
    { dataKey: "allowOneWay", header: "Allow One Way in Remnant" },
    { dataKey: "layType", header: "Lay Type" },
    { dataKey: "cutAll", header: "Cut All" },
    { dataKey: "cutPercent", header: "Extra Cut (%)" }
  ];
  if (cutOrder.groupList?.every(g => isEmpty(g.csv) || g.csv === "No" || g.csv === "Grade 4")) {
    columnList = columnList.filter(c => c.dataKey !== "allowOneWay");
  }
  if (isEmpty(cutOrder?.inseams) || listFromCsv(cutOrder.inseams)?.length <= 1) {
    columnList = columnList.filter(c => c.dataKey !== "layType");
  }

  const setting = cutOrder.orderConfig?.setting || {};
  let reservation = "";
  if (!isEmpty(setting.reserveType) && !isEmpty(setting.reserveQty)) {
    reservation = setting.reserveQty + " meters " + ReserveType.PER_ROLL_FIXED === setting.reserveType ? " per Roll" : setting.reserveType;
  }

  const body = [
    {
      maxWay: setting.maxPieces,
      fabricDirection: setting.fabricDirection || "-",
      discardedWays: setting.discardedWays || "-",
      reservation: reservation || "-",
      allowOneWay: isTrue(setting.allowOneWay) ? "Yes" : "No",
      layType: setting.layType || "-",
      cutAll: isTrue(setting.cutAll) ? "Yes" : "No",
      cutPercent: ""
    }
  ];

  return { columns: columnList, body };
};

const requiredCols = ["markerNo", "plies", "width", "csv"];

export const generateMarkerExportData = ({ tenantName, order, orderFlow, planTabIdx, poInfoColumns, flowInfoColumns, i18nMessages }) => {
  if (isEmpty(order?.flowInfoList) || isEmpty(orderFlow)) return null;

  const excludeFields = ["itemCode", "fabColor", "allocated", "expOrderQty", "status"];

  const finalFlowInfoColumns = flowInfoColumns
    .filter(c => !excludeFields.includes(c.dataKey))
    .map(c => ({ ...c, title: lodashGet(i18nMessages, `resources.cutPlan.fields.flowInfo.${c.dataKey}`, c.title) }));
  const flowCount = order.flowInfoList?.filter(fi => orderFlow?.flowInfoIds?.includes(fi.id))?.length || 0;
  const maxColCount =
    flowCount > 1 ? Math.max(poInfoColumns.length, finalFlowInfoColumns.length) : poInfoColumns.length + finalFlowInfoColumns.length;

  const { columnList, rowList, sizeGroupCells } = getMarkerData(order, orderFlow, planTabIdx, maxColCount);
  const finalMaxColNo = Math.max(maxColCount, columnList.length);

  const poColumns = poInfoColumns.map(c => ({ ...c, title: lodashGet(i18nMessages, `resources.orders.fields.${c.dataKey}`, c.title) }));
  const infoCellList = getStyleInfoData({
    order,
    orderFlow,
    poInfoColumns: poColumns,
    flowInfoColumns: finalFlowInfoColumns,
    maxColNo: finalMaxColNo
  });
  let lastRowNo = infoCellList.map(e => e.rowNo).sort((a, b) => b - a)[0];
  const sbCellList = getSizeBreakupData(order, orderFlow, finalMaxColNo, lastRowNo);
  if (!isEmpty(sbCellList)) {
    lastRowNo = sbCellList.map(e => e.rowNo).sort((a, b) => b - a)[0];
  }

  const finalCellList = [
    {
      rowNo: 1,
      colNo: 1,
      value: tenantName,
      colEnd: finalMaxColNo,
      cellStyle: { hAlign: "center", border: false, bold: true, fontHeight: 24 },
      cellHeight: 32
    },
    {
      rowNo: 2,
      colNo: 1,
      value: "Marker Details",
      colEnd: finalMaxColNo,
      cellStyle: { hAlign: "center", border: false, fontHeight: 18 },
      cellHeight: 32
    },
    ...infoCellList,
    ...sbCellList,
    { rowNo: lastRowNo + 1, colNo: 1, value: "Markers", colEnd: finalMaxColNo, cellStyle: { bold: true } },
    ...sizeGroupCells.map(cell => ({ ...cell, rowNo: lastRowNo + 2 }))
  ];

  const excelSheet = {
    sheetNo: 0,
    sheetName: "Markers",
    autoSizeColumn: orderFlow?.orderConfig?.multiPartBundling === MULTI_PART_MANUAL_PLAN,
    excelData: {
      cellList: finalCellList,
      columnList: columnList.map(c => (requiredCols.includes(c.dataKey) ? { ...c, title: `${c.title}*` } : c)),
      rowList
    }
  };

  return excelSheet;
};

const getStyleInfoData = ({ order, orderFlow, poInfoColumns, flowInfoColumns, maxColNo }) => {
  let cellList = [];

  const flowCount = order.flowInfoList?.filter(fi => orderFlow?.flowInfoIds?.includes(fi.id))?.length || 0;
  if (flowCount > 1) {
    // PO Details
    const poInfo = { ...order, customerName: order?.customer?.name, seasonName: order?.season?.name };
    cellList.push({ rowNo: 3, colNo: 1, colEnd: maxColNo, value: "PO Details", cellStyle: { bold: true } });
    const poEmptyCols = maxColNo > poInfoColumns.length ? maxColNo - poInfoColumns.length : 0;
    cellList = [
      ...cellList,
      ...poInfoColumns.map((c, idx) => ({
        rowNo: 4,
        colNo: idx + 1,
        value: c.title,
        cellStyle: { bgColor: "99,172,238", color: "255,255,255", hAlign: "left" }
      })),
      ...Array(poEmptyCols)
        .fill("")
        .map((_, idx) => ({
          rowNo: 4,
          colNo: poInfoColumns.length + idx + 1,
          cellStyle: { bgColor: "99,172,238", color: "255,255,255", hAlign: "left" }
        }))
    ];
    cellList = [
      ...cellList,
      ...poInfoColumns.map((c, idx) => ({
        rowNo: 5,
        colNo: idx + 1,
        value: poInfo[c.dataKey],
        cellStyle: { hAlign: "left" }
      })),
      ...Array(poEmptyCols)
        .fill("")
        .map((_, idx) => ({ rowNo: 5, colNo: poInfoColumns.length + idx + 1, cellStyle: { hAlign: "left" } }))
    ];

    // Flow Details
    const flowRows = order.flowInfoList
      ?.filter(fi => orderFlow?.flowInfoIds?.includes(fi.id))
      .map(flowInfo => {
        const part = flowInfo.partList.find(p => p.effPartName === orderFlow.part) || {};
        // const ledgerList = flowInfo?.ledgerList?.filter(l => part?.id === l.part?.id);
        // const ledger = ledgerList[0];

        return {
          ...flowInfo,
          productName: flowInfo.product?.name,
          styleColor: flowInfo.color,
          lotValue: `Lot ${orderFlow.lot}`,
          effPartName: orderFlow.part,
          bomCu: part?.bomCu,
          ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
          uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom
          // itemCode: ledger?.fabric?.itemCode,
          // fabColor: ledger?.color
        };
      });
    cellList.push({ rowNo: 6, colNo: 1, colEnd: maxColNo, value: "Flow Details", cellStyle: { bold: true } });
    const flowEmptyCols = maxColNo > flowInfoColumns.length ? maxColNo - flowInfoColumns.length : 0;
    cellList = [
      ...cellList,
      ...flowInfoColumns.map((c, idx) => ({
        rowNo: 7,
        colNo: idx + 1,
        value: c.title,
        cellStyle: { bgColor: "99,172,238", color: "255,255,255", hAlign: "left" }
      })),
      ...Array(flowEmptyCols)
        .fill("")
        .map((_, idx) => ({
          rowNo: 7,
          colNo: flowInfoColumns.length + idx + 1,
          cellStyle: { bgColor: "99,172,238", color: "255,255,255", hAlign: "left" }
        }))
    ];
    flowRows.forEach((flow, flowIdx) => {
      cellList = [
        ...cellList,
        ...flowInfoColumns.map((c, idx) => ({
          rowNo: 8 + flowIdx,
          colNo: idx + 1,
          value: flow[c.dataKey],
          cellStyle: { hAlign: "left" }
        })),
        ...Array(flowEmptyCols)
          .fill("")
          .map((_, idx) => ({
            rowNo: 8 + flowIdx,
            colNo: flowInfoColumns.length + idx + 1,
            cellStyle: { hAlign: "left" }
          }))
      ];
    });
  } else {
    // PO Details
    const columns = [...poInfoColumns, ...flowInfoColumns];

    const poInfo = { ...order, customerName: order?.customer?.name, seasonName: order?.season?.name };
    const flowRows = order.flowInfoList
      ?.filter(fi => orderFlow?.flowInfoIds?.includes(fi.id))
      .map(flowInfo => {
        const part = flowInfo.partList.find(p => p.effPartName === orderFlow.part) || {};
        // const ledgerList = flowInfo?.ledgerList?.filter(l => part?.id === l.part?.id);
        // const ledger = ledgerList[0];

        return {
          ...flowInfo,
          productName: flowInfo.product?.name,
          styleColor: flowInfo.color,
          lotValue: `Lot ${orderFlow.lot}`,
          effPartName: orderFlow.part,
          bomCu: part?.bomCu,
          ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
          uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom
          // itemCode: ledger?.fabric?.itemCode,
          // fabColor: ledger?.color
        };
      });
    const row = { ...poInfo, ...flowRows[0] };

    cellList.push({ rowNo: 3, colNo: 1, colEnd: maxColNo, value: "PO Details", cellStyle: { bold: true } });
    const poEmptyCols = maxColNo > columns.length ? maxColNo - columns.length : 0;
    cellList = [
      ...cellList,
      ...columns.map((c, idx) => ({
        rowNo: 4,
        colNo: idx + 1,
        value: c.title,
        cellStyle: { bgColor: "99,172,238", color: "255,255,255", hAlign: "left" }
      })),
      ...Array(poEmptyCols)
        .fill("")
        .map((_, idx) => ({
          rowNo: 4,
          colNo: columns.length + idx + 1,
          cellStyle: { bgColor: "99,172,238", color: "255,255,255", hAlign: "left" }
        }))
    ];
    cellList = [
      ...cellList,
      ...columns.map((c, idx) => ({
        rowNo: 5,
        colNo: idx + 1,
        value: row[c.dataKey],
        cellStyle: { hAlign: "left" }
      })),
      ...Array(poEmptyCols)
        .fill("")
        .map((_, idx) => ({ rowNo: 5, colNo: columns.length + idx + 1, cellStyle: { hAlign: "left" } }))
    ];
  }

  return cellList;
};

const getSizeBreakupData = (order, orderFlow, maxColNo, lastRowNo) => {
  if (orderFlow?.lotType === LotType.BULK) return [];
  const flowInfoList = getCutOrderFlowInfoList(order, orderFlow);

  let { columns, headerRows } = getColumnsSizeBreakup(flowInfoList);
  const rowList = getRowsSizeBreakup(flowInfoList, order);

  const colStart = orderFlow?.lotType === LotType.BULK ? 3 : 2;
  columns = columns.map((col, idx) => ({ ...col, colNo: colStart + idx }));

  // Process Size Group Header
  const bgColors = ["38,174,135", "73,215,175"]; //							 RGB (73, 215, 175)
  let sizeGroupCells = [];
  if (headerRows.length > 1) {
    const headerRow = headerRows[0];
    sizeGroupCells = Object.keys(headerRow).map(key => {
      const colList = columns.filter(c => c.dataKey.split("_")[0] === key.split("_")[0]);
      const colNo = colList.sort((a, b) => a.colNo - b.colNo)[0]?.colNo;
      const colEnd = colList.sort((a, b) => b.colNo - a.colNo)[0]?.colNo;
      const sizeGroupIdx = colList[0]?.styleColorIdx;
      return {
        colNo,
        colEnd,
        value: headerRow[key],
        cellStyle: { bgColor: bgColors[sizeGroupIdx % 2], color: "255,255,255", hAlign: "center", bold: true }
      };
    });
    const lastCol = sizeGroupCells.sort((a, b) => b.colEnd - a.colEnd)[0]?.colEnd;
    const minCol = sizeGroupCells.sort((a, b) => a.colNo - b.colNo)[0]?.colNo;
    const maxCol = Math.max(maxColNo, columns.length);

    const preEmptyCells = Array(minCol - 1)
      .fill("")
      .map((_, idx) => ({ colNo: idx + 1, cellStyle: { bgColor: "99,172,238", color: "255,255,255", hAlign: "center" } }));
    const postEmptyCells = Array(maxCol - lastCol)
      .fill("")
      .map((_, idx) => ({ colNo: lastCol + idx + 1, cellStyle: { bgColor: "99,172,238", color: "255,255,255", hAlign: "center" } }));
    sizeGroupCells = [...preEmptyCells, ...sizeGroupCells, ...postEmptyCells];
  }
  let sbCellList = [{ rowNo: lastRowNo + 1, colNo: 1, value: "Size Breakup", colEnd: maxColNo, cellStyle: { bold: true } }];
  lastRowNo = lastRowNo + 1;
  if (!isEmpty(sizeGroupCells)) {
    sbCellList = [...sbCellList, ...sizeGroupCells.map(cell => ({ ...cell, rowNo: lastRowNo + 1 }))];
    lastRowNo = lastRowNo + 1;
  }

  // Process Size Header
  const nonRatioFields = ["style", "color", "destination", "deliveryDate", "total"];
  let columnList = columns.map(({ dataKey, title, styleColorIdx = 0 }, idx) => ({
    colNo: idx + colStart,
    dataKey,
    title,
    cellStyle: !nonRatioFields.includes(dataKey)
      ? { bgColor: bgColors[styleColorIdx % 2], color: "255,255,255", hAlign: "center" }
      : { bgColor: "99,172,238", color: "255,255,255", hAlign: "center" }
  }));
  const emptyCols = maxColNo > columnList.length ? maxColNo - columnList.length : 0;
  if (emptyCols > 0) {
    columnList = [
      ...columnList,
      ...Array(emptyCols)
        .fill("")
        .map((_, idx) => ({
          dataKey: `col${idx}`,
          colNo: idx + 1 < colStart ? idx + 1 : columnList.length + idx + 1,
          cellStyle: { bgColor: "99,172,238", color: "255,255,255", hAlign: "center" }
        }))
    ];
  }

  sbCellList = [
    ...sbCellList,
    ...columnList.map(col => ({ rowNo: lastRowNo + 1, colNo: col.colNo, value: col.title, cellStyle: col.cellStyle })),
    ...rowList.flatMap((row, rowIdx) =>
      columnList.map(col => ({
        rowNo: lastRowNo + rowIdx + 2,
        colNo: col.colNo,
        value: row[col.dataKey],
        cellStyle: { hAlign: "center" }
      }))
    )
  ];

  return sbCellList;
};

const getMarkerData = (order, orderFlow, planTabIdx, maxColNo) => {
  let { columns, headerRows } = getColumnsMarkerDetails(order, orderFlow);
  let rowList = getRowsMarkerDetails(order, orderFlow, planTabIdx);
  if (orderFlow?.lotType === LotType.BULK) {
    columns = columns.filter(
      c => !["ply", "qty", "qtyPercentage", "fabric", "groupName", "patternNumber", "layLength", "efficiency", "remarks"].includes(c.dataKey)
    );
    columns = [
      { dataKey: "id", title: "ID" },
      ...columns,
      { dataKey: "qtyPercent", title: "Quantity (%)" },
      { dataKey: "width", title: "Width (CM)" },
      { dataKey: "patternValue", title: "Pattern Value" },
      { dataKey: "patternNumber", title: "Pattern No" },
      { dataKey: "csv", title: "CSV" },
      { dataKey: "length", title: "Marker Length" },
      { dataKey: "efficiency", title: "Efficiency" }
    ].map((c, idx) => ({ ...c, colNo: idx + 1 }));
  } else {
    columns = columns.filter(
      c =>
        ![
          "ply",
          "qty",
          "qtyPercentage",
          "width",
          "patternValue",
          "csv",
          "remarks",
          "fabric",
          "balanceFabric",
          "groupName",
          "patternNumber",
          "layLength",
          "efficiency"
        ].includes(c.dataKey)
    );
    columns = [
      ...columns,
      { dataKey: "qty", title: "Qty" },
      { dataKey: "length", title: "Marker Length" },
      { dataKey: "efficiency", title: "Efficiency" },
      { dataKey: "fabric", title: "Fab. Required" },
      { dataKey: "width", title: "Width (CM)" },
      { dataKey: "warpPattern", title: "Pattern - Length (%)" },
      { dataKey: "weftPattern", title: "Pattern - Width (%)" },
      { dataKey: "patternNumber", title: "Pattern No" },
      { dataKey: "csv", title: "CSV" },
      { dataKey: "remarks", title: "Remarks" }
    ].map((c, idx) => ({ ...c, colNo: idx + 1 }));
  }

  const bgColors = ["38,174,135", "73,215,175"]; //							 RGB (73, 215, 175)
  let sizeGroupCells = [];
  if (headerRows.length > 1) {
    const headerRow = headerRows[0];
    sizeGroupCells = Object.keys(headerRow).map(key => {
      const colList = columns.filter(c => c.dataKey.split("_")[0] === key.split("_")[0]);
      const colNo = colList.sort((a, b) => a.colNo - b.colNo)[0]?.colNo;
      const colEnd = colList.sort((a, b) => b.colNo - a.colNo)[0]?.colNo;
      const sizeGroupIdx = colList[0]?.styleColorIdx;
      return {
        colNo,
        colEnd,
        value: headerRow[key],
        cellStyle: { bgColor: bgColors[sizeGroupIdx % 2], color: "255,255,255", hAlign: "center", bold: true }
      };
    });
    const lastCol = sizeGroupCells.sort((a, b) => b.colEnd - a.colEnd)[0]?.colEnd;
    const minCol = sizeGroupCells.sort((a, b) => a.colNo - b.colNo)[0]?.colNo;
    const maxCol = Math.max(maxColNo, columns.length);
    const preEmptyCells = Array(minCol - 1)
      .fill("")
      .map((_, idx) => ({ colNo: idx + 1, cellStyle: { bgColor: "99,172,238" } }));
    const postEmptyCells = Array(maxCol - lastCol)
      .fill("")
      .map((_, idx) => ({ colNo: lastCol + idx + 1, cellStyle: { bgColor: "99,172,238" } }));
    sizeGroupCells = [...preEmptyCells, ...sizeGroupCells, ...postEmptyCells];
  }

  const nonRatioFields = [
    "id",
    "name",
    "qtyPercent",
    "width",
    "patternValue",
    "patternNumber",
    "csv",
    "markerNo",
    "way",
    "qty",
    "plies",
    "length",
    "efficiency",
    "fabric",
    "balanceFabric",
    "shade",
    "warpPattern",
    "weftPattern",
    "remarks"
  ];
  let columnList = columns.map(({ dataKey, title, styleColorIdx = 0 }, idx) => ({
    colNo: idx + 1,
    dataKey,
    title,
    type: ["name", "patternValue", "patternNumber", "csv", "shade", "remarks"].includes(dataKey)
      ? "string"
      : ["qtyPercent", "length", "efficiency", "width", "warpPattern", "weftPattern", "fabric"].includes(dataKey)
        ? "double"
        : dataKey === "id"
          ? "long"
          : "int",
    headerStyle: !nonRatioFields.includes(dataKey)
      ? { bgColor: bgColors[styleColorIdx % 2], color: "255,255,255", hAlign: "center" }
      : { bgColor: "99,172,238", color: "255,255,255", hAlign: "center" },
    cellStyle: { hAlign: "center" }
  }));

  const emptyCols = maxColNo > columnList.length ? maxColNo - columnList.length : 0;
  if (emptyCols > 0) {
    columnList = [
      ...columnList,
      ...Array(emptyCols)
        .fill("")
        .map((_, idx) => ({ dataKey: `col${idx}`, colNo: columnList.length + idx + 1 }))
    ];
  }

  rowList = rowList
    .filter(e => !e.totalRow)
    .map((row, idx) => ({
      rowNo: idx + 1,
      cellList: columnList.map(c => ({
        dataKey: c.dataKey,
        value: !nonRatioFields.includes(c.dataKey) ? row[c.dataKey] / row.plies : row[c.dataKey]
      }))
    }));

  return {
    columnList,
    rowList: isEmpty(rowList)
      ? Array(10)
          .fill("")
          .map((_, idx) => ({ rowNo: idx + 1, cellList: [] }))
      : rowList,
    sizeGroupCells
  };
};

// Effective markers for cutQty calculation by removing duplicates in case of product wise marker
export const getMarkerList = cutOrder => {
  if (isEmpty(cutOrder?.markerList)) return [];
  const normalMarkers = cutOrder.markerList.filter(m => isEmpty(m.productId));
  const productMarkers = cutOrder.markerList.filter(m => !isEmpty(m.productId));
  return [...normalMarkers, ...getDistinctObjectValues(productMarkers, "markerNo")];
};
